import { Injectable, Injector } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { BehaviorSubject, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { IClippingStudioRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-repository";
import { IClippingStudioTranscriptRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-transcript-repository";
import { ClippingStudioSubtitleStyle } from "app/js/src/domain/clipping-studio/clipping-studio-transcript";
import { ClippingStudioEditorMode } from "app/js/src/domain/clipping-studio/clipping-studio-editor-mode";
import { ClippingStudioClip } from "app/js/src/domain/clipping-studio/clipping-studio-clip";
import { ClippingStudioMarker } from "app/js/src/domain/clipping-studio/clipping-studio-marker";
import {
  ClippingStudioProject,
  ClippingStudioSubtitleEvent,
  ClippingStudioTextEvent,
  ClippingStudioGraphicEvent,
  DestinationVariant,
  ReframeConfig,
  ReframeKeyframe,
  normalizeSegmentReframes,
  AppendedMamSegment,
  type GraphicType,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import {
  ClippingStudioState,
  ClippingStudioUndoableSnapshot,
  createInitialClippingStudioState,
  getUndoableSnapshot,
  applyUndoableSnapshot,
} from "app/js/src/domain/clipping-studio/clipping-studio-state";
import { normalizeTextWeight } from "app/js/src/domain/clipping-studio/clipping-studio-text-layout";
import { ClippingStudioAiReframeService } from "./clipping-studio-ai-reframe.service";
import { MediaRepository } from "app/js/src/domain/media-repository";
import { AudioRepository } from "app/js/src/domain/audio-repository";
import { SubtitleRepository } from "app/js/src/domain/subtitle-repository";
import { resolveDefaultAudioForMedia } from "./clipping-studio-default-audio";
import {
  readPersistedMamAudioMediaId,
  resolveMamAudioRuntimeFields,
} from "./clipping-studio-mam-audio";
import {
  clearMainSourceAudioEvent,
  resolveMainAudioSelection,
  upsertMainSourceAudioTrack,
  type ClippingStudioMainAudioTrack,
} from "./clipping-studio-main-audio";
import {
  allocateImportedSubtitleTrackKey,
  isImportedSubtitleTrackKey,
  mediaSubtitleJsonToStudioCues,
  MediaSubtitleFileJson,
  normalizeSubtitleLanguageKey,
  pickOneSubtitleTrackPerLanguage,
  pruneOrphanSubtitleEdits,
} from "./clipping-studio-media-subtitle-loader";
import { AspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";
import { MediaInfo } from "app/js/src/domain/media";
import { PublishMetadataSavePayload } from "app/js/src/domain/clipping-studio/i-clipping-studio-repository";
import { PublishStateService } from "./publish-state.service";
import {
  ClipPreviewAspectRatio,
  ClipPreviewEditorState,
  createVideoSnapshotClipPreviewModel,
  getClipPreviewDisplayUrl,
  hasClipPreviewModel,
  parseClipPreviewModel,
  serializeClipPreviewModel,
  updateClipPreviewAfterEditorApply,
} from "app/js/src/domain/clipping-studio/clip-preview-model";
import { resolveVideoSnapshotEditorSource } from "app/js/src/domain/clipping-studio/clip-preview-image-encode";

/** Set to true to log seek flow (e.g. after reorder). */
const DEBUG_SEEK = false;
function csSeekLog(...args: unknown[]): void {
  if (DEBUG_SEEK) console.log("[CS seek manager]", ...args);
}

/** UI indicator for project persist (autosave / explicit save). */
export type ClippingStudioProjectSaveStatus = "saved" | "saving" | "error";

@Injectable()
export class ClippingStudioManagerService {
  private stateSubject = new BehaviorSubject<ClippingStudioState>(
    createInitialClippingStudioState(),
  );
  public state$ = this.stateSubject.asObservable();

  private saveStatusSubject =
    new BehaviorSubject<ClippingStudioProjectSaveStatus>("saved");
  /** Latest project-save outcome for the top-bar indicator. */
  public saveStatus$ = this.saveStatusSubject.asObservable();
  /** Reactive save status for templates. */
  readonly saveStatus = toSignal(this.saveStatus$, {
    initialValue: "saved" as ClippingStudioProjectSaveStatus,
  });
  /** Monotonic id so only the latest in-flight save updates the indicator. */
  private saveGeneration = 0;

  /** Effective project fps (from project or media). Signal for reactive use in templates. */
  readonly projectFps = toSignal(
    this.state$.pipe(
      map((s) => s.fps ?? s.media?.fps ?? 30),
    ),
    { initialValue: 30 },
  );

  /** Backend project id when loaded; null before load. Signal so find-clip panel can react and load recent prompts. */
  readonly projectId = toSignal(
    this.state$.pipe(map((s) => s.projectId)),
    { initialValue: null as number | null },
  );

  /** Find-clip recent prompts (JSON string for project save). Signal for reactive use. */
  readonly findClipRecentPrompts = toSignal(
    this.state$.pipe(map((s) => s.findClipRecentPrompts)),
    { initialValue: null as string | null },
  );

  /** Called by find-clip panel when the list of recent prompts changes so project save sends the correct data. */
  setFindClipRecentPrompts(prompts: string[]): void {
    const trimmed = prompts.filter((p) => typeof p === "string" && p.trim());
    const json =
      trimmed.length === 0
        ? "[]"
        : JSON.stringify(
            trimmed.map((text) => ({
              text: text.trim(),
              created_at: new Date().toISOString(),
            })),
          );
    this.updateState({ findClipRecentPrompts: json });
  }

  /** Notifies when a segment was added from a panel (e.g. Find Clip). Subscribers can capture thumbnails. Bypasses template output chain. */
  readonly segmentAddedFromPanel$ = new Subject<string>();

  /** Call from panel when a segment was added so the host can capture thumbnails. */
  notifySegmentAddedFromPanel(clipId: string): void {
    this.segmentAddedFromPanel$.next(clipId);
  }

  private playRafId: number | null = null;
  private playLastFrameTime: number = 0;
  private playbackRate = 1;

  private _undoStack: ClippingStudioUndoableSnapshot[] = [];
  private _redoStack: ClippingStudioUndoableSnapshot[] = [];
  private static readonly MAX_UNDO_STACK = 50;

  /** Current preview destination id (null = Original). Set by the component to avoid circular DI with PublishStateService. */
  private _currentPreviewDestinationId: string | null = null;

  /** Destination id -> aspect ratio (e.g. "9:16"). Set by the component so manager can run AI reframe without depending on PublishStateService. */
  private _destinationAspectRatios: Record<string, AspectRatio> = {};

  /** When set (SOURCE + zoom in, not FIT), seek and playback are clamped to this segment. Set by the component. */
  private _zoomedSegmentBounds: { startMs: number; endMs: number } | null = null;

  /** Segment index (0 = main, 1+ = MAM) and fit flag. When fit is true or index null, bounds are cleared. */
  private _zoomedSegmentIndex: number | null = null;
  private _zoomedSegmentFit = true;

  private clipPreviewFrameProvider: ((
    timeMs: number,
    options?: {
      overlayTimeMs?: number | null;
      destinationUid?: string | null;
      aspectRatio?: ClipPreviewAspectRatio;
    },
  ) => Promise<string | null>) | null = null;

  private capturingClipPreviewSubject = new BehaviorSubject<boolean>(false);
  /** True while a clip-preview snapshot (transcoded / composed) is in progress. */
  readonly isCapturingClipPreview$ = this.capturingClipPreviewSubject.asObservable();
  get isCapturingClipPreview(): boolean {
    return this.capturingClipPreviewSubject.value;
  }
  setCapturingClipPreview(capturing: boolean): void {
    this.capturingClipPreviewSubject.next(!!capturing);
  }

  /** Presigned mezzanine URL for thumbnail / WYSIWYG snapshot capture. */
  getMediaTranscodedUrl(mediaId: number): Promise<string> {
    return this.mediaRepository.getMediaTranscodedUrl(mediaId);
  }

  constructor(
    private repository: IClippingStudioRepository,
    private transcriptRepository: IClippingStudioTranscriptRepository,
    private aiReframeService: ClippingStudioAiReframeService,
    private mediaRepository: MediaRepository,
    private audioRepository: AudioRepository,
    private subtitleRepository: SubtitleRepository,
    private injector: Injector,
  ) {}

  /** Called by the component to provide destination aspect ratios for AI reframe. */
  setDestinationAspectRatios(map: Record<string, AspectRatio>): void {
    this._destinationAspectRatios = { ...map };
  }

  setClipPreviewFrameProvider(
    provider: ((
      timeMs: number,
      options?: {
        overlayTimeMs?: number | null;
        destinationUid?: string | null;
        aspectRatio?: ClipPreviewAspectRatio;
      },
    ) => Promise<string | null>) | null,
  ): void {
    this.clipPreviewFrameProvider = provider;
  }

  /** Called by the component when zoomed into a segment in SOURCE mode (not FIT). When set, seek and playback are clamped to [startMs, endMs]. Pass null when not zoomed or in FIT. */
  setZoomedSegmentBounds(bounds: { startMs: number; endMs: number } | null): void {
    this._zoomedSegmentBounds = bounds;
  }

  /**
   * Called by the component to sync zoom state. Manager computes bounds from its own state so they are always correct.
   * Bounds are set whenever we are viewing a single segment (segmentIndex != null), so step frame and playback stay within that segment.
   * Bounds are cleared only in global FIT (segmentIndex null), when we see all sources on the timeline at once.
   */
  setZoomedSegment(segmentIndex: number | null, _segmentFit: boolean): void {
    this._zoomedSegmentIndex = segmentIndex;
    this._zoomedSegmentFit = _segmentFit;
    this.updateZoomedSegmentBoundsFromState();
  }

  private updateZoomedSegmentBoundsFromState(): void {
    if (
      this._zoomedSegmentIndex == null ||
      this.state?.mode !== "SOURCE" ||
      (this.state?.appendedMamSegments?.length ?? 0) === 0
    ) {
      this._zoomedSegmentBounds = null;
      return;
    }
    const startMs = this.getSegmentStartMsInternal(this._zoomedSegmentIndex);
    const endMs = this.getSegmentEndMsInternal(this._zoomedSegmentIndex);
    this._zoomedSegmentBounds = { startMs, endMs };
  }

  private getSegmentStartMsInternal(segmentIndex: number): number {
    if (segmentIndex <= 0) return 0;
    const mainMs = this.state?.media?.durationMs ?? 0;
    const segments = this.state?.appendedMamSegments ?? [];
    if (segmentIndex === 1) return mainMs;
    let cursor = mainMs;
    for (let i = 0; i < segmentIndex - 1 && i < segments.length; i++) {
      cursor += segments[i].durationMs;
    }
    return cursor;
  }

  private getSegmentEndMsInternal(segmentIndex: number): number {
    const mainMs = this.state?.media?.durationMs ?? 0;
    const segments = this.state?.appendedMamSegments ?? [];
    const segmentDurationsMs: number[] = [mainMs];
    for (const seg of segments) {
      segmentDurationsMs.push(seg.durationMs);
    }
    const idx = Math.min(segmentIndex, segmentDurationsMs.length - 1);
    let end = 0;
    for (let i = 0; i <= idx; i++) end += segmentDurationsMs[i];
    return end;
  }

  private resolveAppendedMamWindow(
    startMs: number,
    endMs: number,
    appended: AppendedMamSegment[] = this.state.appendedMamSegments ?? [],
    mainDurationMs: number = this.state.media?.durationMs ?? 0,
  ): { segment: AppendedMamSegment; startMs: number; endMs: number } | null {
    if (startMs < mainDurationMs || endMs <= mainDurationMs) return null;
    let cursorMs = mainDurationMs;
    for (const segment of appended) {
      const segmentEndMs = cursorMs + segment.durationMs;
      if (startMs >= cursorMs && endMs <= segmentEndMs) {
        return { segment, startMs: cursorMs, endMs: segmentEndMs };
      }
      cursorMs = segmentEndMs;
    }
    return null;
  }

  private annotateClipSourceFromAppendedMam(
    clip: ClippingStudioClip,
    appended: AppendedMamSegment[] = this.state.appendedMamSegments ?? [],
    mainDurationMs: number = this.state.media?.durationMs ?? 0,
  ): ClippingStudioClip {
    if (clip.mamMediaId != null) return clip;
    const appendedWindow = this.resolveAppendedMamWindow(
      clip.inMs,
      clip.outMs,
      appended,
      mainDurationMs,
    );
    if (appendedWindow == null) return clip;
    return {
      ...clip,
      mamMediaId: appendedWindow.segment.mamMediaId,
      previewUrl: appendedWindow.segment.previewUrl,
      appendedSegmentId: appendedWindow.segment.id,
      ...this.mamAudioFieldsFromSelection(appendedWindow.segment),
    };
  }

  /** Maps persisted/runtime MAM audio selection onto clip/segment preview fields. */
  private mamAudioFieldsFromSelection(source: {
    audioMediaId?: number;
    defaultAudioMediaId?: number;
    defaultAudioPreviewUrl?: string;
  }): Pick<
    ClippingStudioClip,
    "audioMediaId" | "defaultAudioMediaId" | "defaultAudioPreviewUrl"
  > {
    const audioMediaId = readPersistedMamAudioMediaId(source);
    return {
      ...(audioMediaId != null ? { audioMediaId, defaultAudioMediaId: audioMediaId } : {}),
      ...(source.defaultAudioPreviewUrl != null &&
        source.defaultAudioPreviewUrl !== "" && {
          defaultAudioPreviewUrl: source.defaultAudioPreviewUrl,
        }),
    };
  }

  private mamAudioFieldsFromRuntime(runtime: {
    audioMediaId: number | undefined;
    defaultAudioPreviewUrl: string | undefined;
  }): Pick<
    ClippingStudioClip,
    "audioMediaId" | "defaultAudioMediaId" | "defaultAudioPreviewUrl"
  > {
    if (runtime.audioMediaId == null || runtime.audioMediaId <= 0) {
      return {
        audioMediaId: undefined,
        defaultAudioMediaId: undefined,
        defaultAudioPreviewUrl: undefined,
      };
    }
    const preview = runtime.defaultAudioPreviewUrl?.trim();
    return {
      audioMediaId: runtime.audioMediaId,
      defaultAudioMediaId: runtime.audioMediaId,
      defaultAudioPreviewUrl: preview ? preview : undefined,
    };
  }

  get state(): ClippingStudioState {
    return this.stateSubject.value;
  }

  /** Total timeline duration. In SOURCE mode: main + appended MAM. In EDIT mode with clips: sum of clip lengths (global edit timeline). */
  getTotalTimelineDurationMs(): number {
    if (this.state.mode === "EDIT" && (this.state.clips?.length ?? 0) > 0) {
      const editDuration = this.getEditTimelineDurationMs();
      if (editDuration > 0) return editDuration;
    }
    const main = this.state.media?.durationMs ?? 0;
    const appended = this.state.appendedMamSegments ?? [];
    const appendedTotal = appended.reduce((s, seg) => s + seg.durationMs, 0);
    return main + appendedTotal;
  }

  /** Edit mode only: total length of the global edit timeline (sum of all clip lengths in order). */
  getEditTimelineDurationMs(): number {
    const ordered = this.getOrderedClips();
    if (ordered.length === 0) return 0;
    return ordered.reduce((s, c) => s + (c.outMs - c.inMs), 0);
  }

  /** Current time in source space (for playback/video). In EDIT mode with clips: derived from edit time. Otherwise: state.currentTimeMs. */
  getCurrentSourceTimeMs(): number {
    if (
      this.state.mode === "EDIT" &&
      this.state.clips &&
      this.state.clips.length > 0
    ) {
      return this.editToSourceTimeMs(this.state.currentTimeMs);
    }
    return this.state.currentTimeMs;
  }

  /** Clip at current playhead (EDIT mode with clips); null otherwise. */
  getClipAtPlayhead(): ClippingStudioClip | null {
    if (
      this.state.mode !== "EDIT" ||
      !this.state.clips ||
      this.state.clips.length === 0
    ) {
      return null;
    }
    return this.getClipContainingSourceTime(this.getCurrentSourceTimeMs());
  }

  /** Convert source time to edit timeline position (0..getEditTimelineDurationMs()). Public for panels that seek by source time in EDIT mode. */
  sourceToEditTimeMs(sourceMs: number): number {
    return this.sourceToEditTimeMsInternal(sourceMs);
  }

  get currentPreviewDestinationId(): string | null {
    return this._currentPreviewDestinationId;
  }

  /** Called by the component when the aspect-ratio dropdown changes. Keeps manager in sync without depending on PublishStateService. */
  setCurrentPreviewDestinationId(id: string | null): void {
    this._currentPreviewDestinationId = id;
  }

  /**
   * Effective text for a destination: base merged with overrides plus destination-only.
   * When destId is null (Original), returns base text only.
   */
  getEffectiveText(state: ClippingStudioState, destId: string | null): ClippingStudioTextEvent[] {
    const base = state.text ?? [];
    if (destId == null) return [...base];
    const variant = state.destinationVariants?.[destId];
    if (!variant) return [...base];
    const overrides = variant.textOverrides ?? {};
    const merged = base.map((t) => {
      const ov = overrides[t.id];
      return ov ? { ...t, ...ov } : t;
    });
    const only = variant.textOnly ?? [];
    const excluded = new Set(variant.textExcludedIds ?? []);
    return [...merged, ...only].filter((t) => !excluded.has(t.id));
  }

  /**
   * Effective graphics for a destination: base merged with overrides plus destination-only.
   * When destId is null (Original), returns base graphics only.
   */
  getEffectiveGraphics(state: ClippingStudioState, destId: string | null): ClippingStudioGraphicEvent[] {
    const base = state.graphics ?? [];
    if (destId == null) return [...base];
    const variant = state.destinationVariants?.[destId];
    if (!variant) return [...base];
    const overrides = variant.graphicOverrides ?? {};
    const merged = base.map((g) => {
      const ov = overrides[g.id];
      return ov ? { ...g, ...ov } : g;
    });
    const only = variant.graphicOnly ?? [];
    const excluded = new Set(variant.graphicExcludedIds ?? []);
    return [...merged, ...only].filter((g) => !excluded.has(g.id));
  }

  get canUndo(): boolean {
    return this._undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this._redoStack.length > 0;
  }

  undoOperation(): void {
    if (this._undoStack.length === 0) return;
    const snapshot = this._undoStack.pop();
    this._redoStack.push(getUndoableSnapshot(this.state));
    const partial = applyUndoableSnapshot(snapshot);
    this.updateState(partial);
    this.recomputeSubtitleTracksIfNeeded();
    this.persistProject();
  }

  redoOperation(): void {
    if (this._redoStack.length === 0) return;
    const snapshot = this._redoStack.pop();
    this._undoStack.push(getUndoableSnapshot(this.state));
    const partial = applyUndoableSnapshot(snapshot);
    this.updateState(partial);
    this.recomputeSubtitleTracksIfNeeded();
    this.persistProject();
  }

  private recomputeSubtitleTracksIfNeeded(): void {
    const edits = this.state.subtitleEdits;
    if (!edits) return;
    const originals = this.state.subtitleOriginalsByLanguage ?? {};
    const tracksByLanguage = this.buildSubtitleTracksByLanguage(
      originals,
      edits,
    );
    const activeLang = normalizeSubtitleLanguageKey(
      this.state.activeSubtitleLanguage,
    );
    this.updateState({
      subtitleTracksByLanguage: tracksByLanguage,
      subtitles: activeLang
        ? [...(tracksByLanguage[activeLang] ?? [])]
        : this.state.subtitles,
    });
  }

  /** Merge originals ⊕ edits for every language present in either map. */
  private buildSubtitleTracksByLanguage(
    originals: Record<string, ClippingStudioSubtitleEvent[]>,
    edits: Record<string, ClippingStudioSubtitleEvent[]>,
  ): Record<string, ClippingStudioSubtitleEvent[]> {
    const langs = new Set([
      ...Object.keys(originals ?? {}),
      ...Object.keys(edits ?? {}),
    ]);
    const tracksByLanguage: Record<string, ClippingStudioSubtitleEvent[]> = {};
    for (const lang of langs) {
      tracksByLanguage[lang] = this.mergeSubtitleEdits(
        originals[lang] ?? [],
        edits[lang],
      );
    }
    return tracksByLanguage;
  }

  /** Language key for write paths (active subtitle → transcript → existing maps). */
  private resolveActiveSubtitleEditLanguage(
    preferred?: string | null,
  ): string | undefined {
    const candidates = [
      preferred,
      this.state.activeSubtitleLanguage,
      this.state.activeTranscriptLanguage,
      Object.keys(this.state.subtitleTracksByLanguage ?? {})[0],
      Object.keys(this.state.subtitleOriginalsByLanguage ?? {})[0],
      Object.keys(this.state.subtitleEdits ?? {})[0],
    ];
    for (const candidate of candidates) {
      const key = normalizeSubtitleLanguageKey(candidate);
      if (key) return key;
    }
    return undefined;
  }

  /**
   * Apply next subtitleEdits, rebuild runtime tracks + active flat view.
   * Flat `subtitles` is runtime-only; callers persist edits via persistProject.
   */
  private commitSubtitleEdits(
    nextEdits: Record<string, ClippingStudioSubtitleEvent[]>,
    extras?: Partial<ClippingStudioState>,
  ): void {
    const originals = this.state.subtitleOriginalsByLanguage ?? {};
    const tracksByLanguage = this.buildSubtitleTracksByLanguage(
      originals,
      nextEdits,
    );
    const preferred =
      extras?.activeSubtitleLanguage ?? this.state.activeSubtitleLanguage;
    const activeLang =
      normalizeSubtitleLanguageKey(preferred) ||
      Object.keys(tracksByLanguage)[0] ||
      undefined;
    this.updateState({
      subtitleEdits: nextEdits,
      subtitleTracksByLanguage: tracksByLanguage,
      ...(activeLang ? { activeSubtitleLanguage: activeLang } : {}),
      subtitles: activeLang
        ? [...(tracksByLanguage[activeLang] ?? [])]
        : [],
      ...extras,
    });
  }

  private shiftSubtitleCuesAfterRemovedWindow(
    cues: ClippingStudioSubtitleEvent[],
    segmentStartMs: number,
    segmentEndMs: number,
    durationMs: number,
  ): ClippingStudioSubtitleEvent[] {
    const overlaps = (startMs: number, endMs: number): boolean =>
      !(endMs <= segmentStartMs || startMs >= segmentEndMs);
    const isTombstone = (s: ClippingStudioSubtitleEvent): boolean =>
      s.Text === "" && s.EndTimeMS <= s.StartTimeMS;

    return cues
      .filter((s) => {
        if (isTombstone(s)) {
          return !(
            s.StartTimeMS >= segmentStartMs && s.StartTimeMS < segmentEndMs
          );
        }
        return !overlaps(s.StartTimeMS, s.EndTimeMS);
      })
      .map((s) => {
        let StartTimeMS = s.StartTimeMS;
        let EndTimeMS = s.EndTimeMS;
        if (StartTimeMS > segmentEndMs) StartTimeMS -= durationMs;
        if (EndTimeMS > segmentEndMs) EndTimeMS -= durationMs;
        return { ...s, StartTimeMS, EndTimeMS };
      })
      .filter((s) => isTombstone(s) || s.StartTimeMS < s.EndTimeMS);
  }

  private pushUndoSnapshot(): void {
    this._undoStack.push(getUndoableSnapshot(this.state));
    if (this._undoStack.length > ClippingStudioManagerService.MAX_UNDO_STACK) {
      this._undoStack.shift();
    }
    this._redoStack = [];
  }

  /**
   * Hydrates studio state from a create-project response (router state) without legacy GET by mediaId.
   */
  async openProject(projectId: number, project: ClippingStudioProject): Promise<void> {
    this._undoStack = [];
    this._redoStack = [];
    const media = this.buildMediaFromProjectMainSource(project);
    this.hydrateStateFromProjectPayload(projectId, project, media);
    await this.refreshSourcePreviewUrlsFromApi();
    if (media?.id != null) {
      await this.loadTranscriptTracks(media.id);
      await this.loadSubtitleTracksAndMerge(media.id, project.subtitleEdits ?? {});
    }
    try {
      const publishState = this.injector.get(PublishStateService);
      await publishState.bootstrapGlobalClipPreviewFromMamSnapshot();
    } catch {
      // PublishStateService may be unavailable in tests.
    }
    if (!hasClipPreviewModel(this.state.clipPreview)) {
      this.initialClipPreviewSnapshotRequestSubject.next();
    }
    this.applyAiReframeFillMissing();
    this.refreshMamClipPreviewUrlsInBackground();
  }

  private buildMediaFromProjectMainSource(
    project: ClippingStudioProject,
  ): ClippingStudioState["media"] {
    const mediaId = project.mediaId;
    let previewUrl = "";
    let durationMs = 0;
    let videoSlug: string | undefined;
    let focusUrl: string | undefined;
    let thumbnailsSpriteUrl = "";
    let thumbnailsSpriteWebVTTUrl = "";
    if (project.mainSource?.trim()) {
      try {
        const ms = JSON.parse(project.mainSource) as Record<string, unknown>;
        previewUrl = String(ms.preview_url ?? "");
        durationMs = Number(ms.duration_ms ?? 0) || 0;
        if (ms.focus_url != null && ms.focus_url !== "") {
          focusUrl = String(ms.focus_url);
        }
        if (ms.thumbnails_sprite_url != null && ms.thumbnails_sprite_url !== "") {
          thumbnailsSpriteUrl = String(ms.thumbnails_sprite_url);
        }
        if (ms.thumbnails_sprite_webvtt_url != null && ms.thumbnails_sprite_webvtt_url !== "") {
          thumbnailsSpriteWebVTTUrl = String(ms.thumbnails_sprite_webvtt_url);
        }
        if (ms.video_slug != null && ms.video_slug !== "") {
          videoSlug = String(ms.video_slug);
        }
      } catch {
        // keep defaults
      }
    }
    const fps = project.fps != null && project.fps > 0 ? project.fps : 30;
    const name = project.title ?? `media-${mediaId}`;
    return {
      id: mediaId,
      name,
      previewUrl,
      focusUrl,
      thumbnailsSpriteUrl,
      thumbnailsSpriteWebVTTUrl,
      fps,
      durationMs,
      videoSlug,
    };
  }

  private hydrateStateFromProjectPayload(
    projectId: number | null,
    project: ClippingStudioProject,
    media: ClippingStudioState["media"],
  ): void {
    const mappedMarkers = (project.markers ?? []).map((m) => ({
      id: m.id,
      inMs: m.inMs,
      text: m.text,
      ...(m.appendedSegmentId != null && { appendedSegmentId: m.appendedSegmentId }),
    }));
    const appended = (project.appendedMamSegments ?? []).map((s) => ({
      ...s,
      ...this.mamAudioFieldsFromSelection(s),
    }));
    const mainDurationMs = media?.durationMs ?? 0;
    const existingMamSegmentIds = new Set(
      mappedMarkers
        .filter((m) => m.appendedSegmentId)
        .map((m) => m.appendedSegmentId as string),
    );
    let cursorMs = mainDurationMs;
    const mamMarkersToAdd: ClippingStudioMarker[] = [];
    for (const seg of appended) {
      if (!existingMamSegmentIds.has(seg.id)) {
        mamMarkersToAdd.push({
          id: `marker-mam-${seg.id}`,
          inMs: cursorMs,
          text: seg.label ?? "MAM source",
          appendedSegmentId: seg.id,
        });
      }
      cursorMs += seg.durationMs;
    }
    const markers = [...mappedMarkers, ...mamMarkersToAdd];

    const resolvedFps =
      project.fps != null && project.fps > 0
        ? project.fps
        : (media?.fps ?? 30);
    this.updateState({
      media,
      projectId,
      mainSource: project.mainSource ?? null,
      findClipRecentPrompts: project.findClipRecentPrompts ?? null,
      fps: resolvedFps,
      projectTitle: project.title ?? media?.name ?? "Untitled",
      clipPreview: project.clipPreview ?? null,
      subtitleStyle: project.subtitleStyle ?? null,
      currentTimeMs: 0,
      clips: (project.video ?? [])
        .map((v) => ({
          id: v.id,
          label: v.label,
          order: v.order,
          inMs: v.inMs,
          outMs: v.outMs,
          thumbnailFirstFrameUrl: v.thumbnailFirstFrameUrl ?? null,
          thumbnailLastFrameUrl: v.thumbnailLastFrameUrl ?? null,
          ...(v.mamMediaId != null && { mamMediaId: v.mamMediaId }),
          ...(v.previewUrl != null && v.previewUrl !== "" && { previewUrl: v.previewUrl }),
          ...(v.appendedSegmentId != null && { appendedSegmentId: v.appendedSegmentId }),
          ...this.mamAudioFieldsFromSelection(v),
        }))
        .map((clip) => this.annotateClipSourceFromAppendedMam(clip, appended, mainDurationMs)),
      markers,
      appendedMamSegments: appended,
      subtitles: [],
      text: [...(project.text ?? [])],
      graphics: [...(project.graphics ?? [])],
      audio: [...(project.audio ?? [])],
      mainAudioTracks: [],
      destinationVariants: project.destinationVariants
        ? Object.fromEntries(
            Object.entries(project.destinationVariants).map(([k, v]) => [
              k,
              {
                textOverrides: v.textOverrides ? { ...v.textOverrides } : undefined,
                graphicOverrides: v.graphicOverrides ? { ...v.graphicOverrides } : undefined,
                textOnly: v.textOnly?.map((t) => ({ ...t })) ?? undefined,
                graphicOnly: v.graphicOnly?.map((g) => ({ ...g })) ?? undefined,
                textExcludedIds: v.textExcludedIds ? [...v.textExcludedIds] : undefined,
                graphicExcludedIds: v.graphicExcludedIds ? [...v.graphicExcludedIds] : undefined,
                segmentReframes: v.segmentReframes
                  ? (() => {
                      const normalized = normalizeSegmentReframes(
                        v.segmentReframes as Record<string, ReframeConfig | ReframeKeyframe[]>,
                      );
                      return Object.fromEntries(
                        Object.entries(normalized).map(([segId, keyframes]) => [
                          segId,
                          keyframes.map((kf) => ({
                            timeMs: kf.timeMs,
                            config: {
                              ...kf.config,
                              crop: kf.config.crop ? { ...kf.config.crop } : undefined,
                              topCrop: kf.config.topCrop ? { ...kf.config.topCrop } : undefined,
                              bottomCrop: kf.config.bottomCrop ? { ...kf.config.bottomCrop } : undefined,
                            },
                          })),
                        ]),
                      );
                    })()
                  : undefined,
              },
            ]),
          )
        : {},
    });
  }

  private async tryBuildMainSourceFromMam(
    mediaId: number,
    fallbackName: string,
  ): Promise<{ mainSourceJson: string; media: ClippingStudioState["media"] } | null> {
    try {
      const [previewUrl, info] = await Promise.all([
        this.mediaRepository.getMediaPreviewFileUrl(mediaId).catch(() => "" as string),
        this.mediaRepository.getMediaInfo(mediaId).catch(() => null as unknown as MediaInfo | null),
      ]);

      // If we can't get a preview URL, we won't be able to show video in the preview anyway.
      if (!previewUrl?.trim()) return null;

      const name = info?.name?.trim() || fallbackName;
      const durationMs = Number(info?.contentMediaDurationMs ?? 0) || 0;
      const fps = Number(info?.contentFps ?? 0) || 30;

      const mainSource = {
        preview_url: previewUrl,
        duration_ms: durationMs,
      } as Record<string, unknown>;

      const mainSourceJson = JSON.stringify(mainSource);
      const nextMedia = {
        ...(this.state.media ?? { id: mediaId }),
        id: mediaId,
        name,
        previewUrl,
        thumbnailsSpriteUrl: this.state.media?.thumbnailsSpriteUrl ?? "",
        thumbnailsSpriteWebVTTUrl: this.state.media?.thumbnailsSpriteWebVTTUrl ?? "",
        fps,
        durationMs,
        videoSlug: this.state.media?.videoSlug,
      };

      return { mainSourceJson, media: nextMedia };
    } catch {
      return null;
    }
  }

  /** Non-blocking: refresh preview URLs for EDIT-mode clips with mamMediaId (stored URLs may be expired). */
  private refreshMamClipPreviewUrlsInBackground(): void {
    const clips = this.state.clips ?? [];
    const mamClips = clips.filter((c) => c.mamMediaId != null);
    if (mamClips.length === 0) return;
    Promise.all(
      mamClips.map(async (c) => {
        if (c.mamMediaId == null) return null;
        try {
          const [url, audioRuntime] = await Promise.all([
            this.mediaRepository.getMediaPreviewFileUrl(c.mamMediaId),
            resolveMamAudioRuntimeFields(
              c.mamMediaId,
              readPersistedMamAudioMediaId(c),
              this.mediaRepository,
              this.audioRepository,
            ),
          ]);
          if (!url?.trim() && audioRuntime.audioMediaId == null && !audioRuntime.shouldPersistSelection) {
            return null;
          }
          return {
            clipId: c.id,
            previewUrl: url?.trim() ? url : undefined,
            audioRuntime,
          };
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      const updates = results.filter((r) => r != null);
      if (updates.length === 0) return;
      const byId = new Map(updates.map((u) => [u.clipId, u]));
      let shouldPersistMamAudio = false;
      const newClips = this.state.clips.map((clip) => {
        const update = byId.get(clip.id);
        if (!update) return clip;
        if (update.audioRuntime.shouldPersistSelection) {
          shouldPersistMamAudio = true;
        }
        return {
          ...clip,
          ...(update.previewUrl ? { previewUrl: update.previewUrl } : {}),
          ...this.mamAudioFieldsFromRuntime(update.audioRuntime),
        };
      });
      this.updateState({ clips: newClips });
      if (shouldPersistMamAudio) {
        this.persistProject();
      } else {
        // Preview URL refresh only — still persist so stale preview URLs are replaced when changed.
        const previewChanged = newClips.some((clip, i) => clip.previewUrl !== clips[i]?.previewUrl);
        if (previewChanged) {
          this.persistProject();
        }
      }
    });
  }

  /**
   * Refreshes presigned preview URLs for SOURCE timeline media (main + appended MAM segments)
   * from the API. Project-persisted URLs may be expired; main playback uses in-memory state.
   * Main audio follows project.audio isMainSource selection (embedded fallback).
   * Appended MAM audio follows persisted audioMediaId with heal-to-first then embedded.
   */
  private async refreshSourcePreviewUrlsFromApi(): Promise<void> {
    const currentMedia = this.state.media;
    let nextMedia = currentMedia;
    let nextAudio = this.state.audio ?? [];
    let nextMainAudioTracks = this.state.mainAudioTracks ?? [];
    let shouldPersistAudioCleanup = false;
    let shouldPersistMamAudio = false;

    if (currentMedia?.id != null && currentMedia.id > 0) {
      try {
        const [url, info] = await Promise.all([
          this.mediaRepository.getMediaPreviewFileUrl(currentMedia.id),
          this.mediaRepository.getMediaInfo(currentMedia.id),
        ]);
        const tracks = this.mapMediaInfoAudioTracks(info);
        nextMainAudioTracks = tracks;
        const resolved = resolveMainAudioSelection(nextAudio, tracks);
        if (resolved.shouldStripStaleEvent) {
          nextAudio = clearMainSourceAudioEvent(nextAudio);
          shouldPersistAudioCleanup = true;
        }
        const mainAudioFields = await this.resolveMainAudioRuntimeFields(
          resolved.selection,
        );
        nextMedia = {
          ...currentMedia,
          ...(url?.trim() ? { previewUrl: url } : {}),
          defaultAudioMediaId: mainAudioFields.defaultAudioMediaId,
          defaultAudioPreviewUrl: mainAudioFields.defaultAudioPreviewUrl,
        };
      } catch {
        // keep URL from project main_source
      }
    }

    const segments = this.state.appendedMamSegments ?? [];
    let nextSegments = segments;
    if (segments.length > 0) {
      nextSegments = await Promise.all(
        segments.map(async (seg) => {
          if (seg.mamMediaId == null || seg.mamMediaId <= 0) {
            return seg;
          }
          try {
            const [url, audioRuntime] = await Promise.all([
              this.mediaRepository.getMediaPreviewFileUrl(seg.mamMediaId),
              resolveMamAudioRuntimeFields(
                seg.mamMediaId,
                readPersistedMamAudioMediaId(seg),
                this.mediaRepository,
                this.audioRepository,
              ),
            ]);
            if (audioRuntime.shouldPersistSelection) {
              shouldPersistMamAudio = true;
            }
            return {
              ...seg,
              ...(url?.trim() ? { previewUrl: url } : {}),
              ...this.mamAudioFieldsFromRuntime(audioRuntime),
            };
          } catch {
            // keep stored segment URL
          }
          return seg;
        }),
      );
    }

    const mediaPreviewChanged =
      nextMedia != null &&
      currentMedia != null &&
      (nextMedia.previewUrl !== currentMedia.previewUrl ||
        nextMedia.defaultAudioPreviewUrl !== currentMedia.defaultAudioPreviewUrl ||
        nextMedia.defaultAudioMediaId !== currentMedia.defaultAudioMediaId);
    const segmentsChanged =
      segments.length > 0 &&
      nextSegments.some(
        (seg, i) =>
          seg.previewUrl !== segments[i].previewUrl ||
          seg.defaultAudioPreviewUrl !== segments[i].defaultAudioPreviewUrl ||
          seg.defaultAudioMediaId !== segments[i].defaultAudioMediaId ||
          seg.audioMediaId !== segments[i].audioMediaId,
      );
    const tracksChanged =
      nextMainAudioTracks.length !== (this.state.mainAudioTracks?.length ?? 0) ||
      nextMainAudioTracks.some(
        (t, i) =>
          t.id !== this.state.mainAudioTracks[i]?.id ||
          t.name !== this.state.mainAudioTracks[i]?.name ||
          t.language !== this.state.mainAudioTracks[i]?.language,
      );
    const audioChanged =
      shouldPersistAudioCleanup ||
      nextAudio.length !== (this.state.audio?.length ?? 0);

    if (!mediaPreviewChanged && !segmentsChanged && !tracksChanged && !audioChanged) {
      return;
    }

    this.updateState({
      ...(mediaPreviewChanged && nextMedia ? { media: nextMedia } : {}),
      ...(segmentsChanged ? { appendedMamSegments: nextSegments } : {}),
      ...(tracksChanged ? { mainAudioTracks: nextMainAudioTracks } : {}),
      ...(audioChanged ? { audio: nextAudio } : {}),
    });

    if (segmentsChanged || shouldPersistAudioCleanup || shouldPersistMamAudio) {
      this.persistProject();
    }
  }

  private mapMediaInfoAudioTracks(
    info: MediaInfo | null | undefined,
  ): ClippingStudioMainAudioTrack[] {
    const files = info?.audioFiles ?? [];
    return files
      .filter((f) => f?.id != null && f.id > 0)
      .map((f) => ({
        id: f.id,
        name: String(f.name ?? "").trim(),
        language: f.language != null ? String(f.language) : null,
      }));
  }

  private async resolveMainAudioRuntimeFields(
    selection: { kind: "track"; mediaId: number } | { kind: "embedded" },
  ): Promise<{
    defaultAudioMediaId: number | undefined;
    defaultAudioPreviewUrl: string | undefined;
  }> {
    if (selection.kind !== "track") {
      return {
        defaultAudioMediaId: undefined,
        defaultAudioPreviewUrl: undefined,
      };
    }
    try {
      const response = await this.audioRepository.getAudioURL(selection.mediaId);
      const url =
        (response && (response.url ?? response.Url)) != null
          ? String(response.url ?? response.Url)
          : "";
      if (!url.trim()) {
        return {
          defaultAudioMediaId: undefined,
          defaultAudioPreviewUrl: undefined,
        };
      }
      return {
        defaultAudioMediaId: selection.mediaId,
        defaultAudioPreviewUrl: url,
      };
    } catch {
      return {
        defaultAudioMediaId: undefined,
        defaultAudioPreviewUrl: undefined,
      };
    }
  }

  /**
   * Sets main-source audio selection. Pass null for embedded (default);
   * pass a linked audio media id to select that track. Persists immediately; not undoable.
   */
  async setMainAudioSelection(mediaId: number | null): Promise<void> {
    const tracks = this.state.mainAudioTracks ?? [];
    let nextAudio = this.state.audio ?? [];
    if (mediaId == null || mediaId <= 0) {
      nextAudio = clearMainSourceAudioEvent(nextAudio);
    } else {
      if (!tracks.some((t) => t.id === mediaId)) {
        return;
      }
      nextAudio = upsertMainSourceAudioTrack(nextAudio, mediaId);
    }
    const resolved = resolveMainAudioSelection(nextAudio, tracks);
    const mainAudioFields = await this.resolveMainAudioRuntimeFields(
      resolved.selection,
    );
    const currentMedia = this.state.media;
    this.updateState({
      audio: nextAudio,
      ...(currentMedia != null
        ? {
            media: {
              ...currentMedia,
              defaultAudioMediaId: mainAudioFields.defaultAudioMediaId,
              defaultAudioPreviewUrl: mainAudioFields.defaultAudioPreviewUrl,
            },
          }
        : {}),
    });
    this.persistProject();
  }

  /** Selected main linked-track id, or null when embedded / default. */
  getMainAudioSelectionMediaId(): number | null {
    const resolved = resolveMainAudioSelection(
      this.state.audio,
      this.state.mainAudioTracks,
    );
    return resolved.selection.kind === "track"
      ? resolved.selection.mediaId
      : null;
  }

  /** Merge originals with edits by id; includes added segments; treats empty Text + zero duration as deleted. */
  private mergeSubtitleEdits(
    originals: ClippingStudioSubtitleEvent[],
    edits: ClippingStudioSubtitleEvent[] | undefined,
  ): ClippingStudioSubtitleEvent[] {
    const editList = edits ?? [];
    const editMap = new Map(editList.map((e) => [e.id, e]));
    const originalIds = new Set(originals.map((o) => o.id));
    const merged = originals
      .map((seg) => {
        const edit = editMap.get(seg.id);
        if (
          edit &&
          edit.Text === "" &&
          edit.EndTimeMS <= edit.StartTimeMS
        ) {
          return null;
        }
        const base = edit ? { ...seg, ...edit } : seg;
        return { ...base, fromMainSource: true };
      })
      .filter((s) => s != null) as ClippingStudioSubtitleEvent[];
    const added = editList
      .filter(
        (e) =>
          !originalIds.has(e.id) &&
          !(e.Text === "" && e.EndTimeMS <= e.StartTimeMS),
      )
      .map((e) => ({ ...e, fromMainSource: false }));
    return [...merged, ...added].sort(
      (a, b) => a.StartTimeMS - b.StartTimeMS,
    );
  }

  private async loadSubtitleTracksAndMerge(
    mediaId: number,
    subtitleEdits: Record<string, ClippingStudioSubtitleEvent[]>,
  ): Promise<void> {
    this.updateState({
      loadingSubtitleTracks: true,
      subtitleLoadError: null,
    });
    try {
      const mediaInfo = await this.mediaRepository.getMediaInfo(mediaId);
      const tracks = pickOneSubtitleTrackPerLanguage(
        mediaInfo?.subtitles ?? [],
      );
      const originals: Record<string, ClippingStudioSubtitleEvent[]> = {};
      const normalizedEdits: Record<string, ClippingStudioSubtitleEvent[]> = {};
      for (const [lang, cues] of Object.entries(subtitleEdits ?? {})) {
        const key = normalizeSubtitleLanguageKey(lang);
        if (!key) continue;
        normalizedEdits[key] = cues;
      }

      for (const track of tracks) {
        const lang = normalizeSubtitleLanguageKey(track.language);
        if (!lang || track.id == null) continue;
        try {
          const signed = await this.subtitleRepository.getSubtitleURL(track.id);
          const url = signed?.url;
          if (!url) {
            originals[lang] = [];
            continue;
          }
          const json = (await this.subtitleRepository.downloadSubtitle(
            url,
          )) as MediaSubtitleFileJson;
          originals[lang] = mediaSubtitleJsonToStudioCues(
            track.id,
            lang,
            json,
          );
        } catch {
          originals[lang] = [];
        }
      }

      const prunedEdits = pruneOrphanSubtitleEdits(originals, normalizedEdits);
      const tracksByLanguage = this.buildSubtitleTracksByLanguage(
        originals,
        prunedEdits,
      );
      const languages = Object.keys(tracksByLanguage);
      const previousActive = normalizeSubtitleLanguageKey(
        this.state.activeSubtitleLanguage,
      );
      const activeLang = languages.includes(previousActive)
        ? previousActive
        : languages[0];

      this.updateState({
        subtitleOriginalsByLanguage: originals,
        subtitleEdits: { ...prunedEdits },
        subtitleTracksByLanguage: tracksByLanguage,
        activeSubtitleLanguage: activeLang,
        subtitles: activeLang
          ? [...(tracksByLanguage[activeLang] ?? [])]
          : [],
        loadingSubtitleTracks: false,
        subtitleLoadError: null,
      });
      // Always persist after open so orphan drops land in DB (no dirty-check skip).
      this.persistProject();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load subtitle tracks.";
      this.updateState({
        loadingSubtitleTracks: false,
        subtitleLoadError: message,
        subtitleTracksByLanguage: undefined,
        activeSubtitleLanguage: undefined,
        subtitleOriginalsByLanguage: undefined,
      });
    }
  }

  setMode(mode: ClippingStudioEditorMode): void {
    if (mode === "EDIT" && (this.state.clips?.length ?? 0) === 0) return;
    this.stop();
    const updates: Partial<ClippingStudioState> = {
      mode,
      pendingInMs: null,
      pendingOutMs: null,
      selectedClipId: null,
      selectedMarkerId: null,
      currentTimeMs: 0,
    };
    this.updateState(updates);
  }

  play(): void {
    if (this.state.isPlaying || !this.state.media) return;
    if (this.state.mode === "EDIT" && this.state.clips.length > 0) {
      const clamped = this.clampSeekToSegmentBounds(this.state.currentTimeMs);
      if (clamped !== this.state.currentTimeMs) {
        this.updateState({ currentTimeMs: clamped });
      }
    } else if (
      this.state.mode === "SOURCE" &&
      this._zoomedSegmentBounds != null
    ) {
      const clamped = this.clampSeekToSegmentBounds(this.state.currentTimeMs);
      if (clamped !== this.state.currentTimeMs) {
        this.updateState({ currentTimeMs: clamped });
      }
    }
    this.updateState({ isPlaying: true });
    this.playLastFrameTime = performance.now();
    this.schedulePlayFrame();
  }

  pause(): void {
    if (!this.state.isPlaying) return;
    this.cancelPlayFrame();
    this.updateState({ isPlaying: false });
  }

  stop(): void {
    this.cancelPlayFrame();
    const atStart =
      this.state.mode === "EDIT" && this.state.clips.length > 0
        ? 0
        : 0;
    this.updateState({ isPlaying: false, currentTimeMs: atStart });
  }

  private schedulePlayFrame(): void {
    this.playRafId = requestAnimationFrame((now) => this.onPlayFrame(now));
  }

  setPlaybackRate(rate: number): void {
    this.playbackRate = Math.max(0.25, Math.min(3, rate));
  }

  getPlaybackRate(): number {
    return this.playbackRate;
  }

  private onPlayFrame(now: number): void {
    if (!this.state.isPlaying || this.playRafId == null) return;
    const deltaMs = now - this.playLastFrameTime;
    this.playLastFrameTime = now;
    const deltaScaled = deltaMs * this.playbackRate;

    if (this.state.mode === "EDIT" && this.state.clips.length > 0) {
      this.advancePlaybackEditMode(deltaScaled);
      return;
    }

    let durationMs: number;
    if (this._zoomedSegmentBounds != null) {
      durationMs = this._zoomedSegmentBounds.endMs;
    } else {
      durationMs = this.getTotalTimelineDurationMs();
    }
    if (durationMs <= 0) {
      this.schedulePlayFrame();
      return;
    }
    const next = this.state.currentTimeMs + deltaScaled;
    if (next >= durationMs) {
      this.cancelPlayFrame();
      this.updateState({ isPlaying: false, currentTimeMs: durationMs });
    } else {
      this.updateState({ currentTimeMs: next });
      this.schedulePlayFrame();
    }
  }

  /** Edit mode: advance by deltaMs in edit time; stop at end. state.currentTimeMs is edit time. */
  private advancePlaybackEditMode(deltaMs: number): void {
    const ordered = this.getOrderedClips();
    if (ordered.length === 0) return;
    const editDurationMs = this.getEditTimelineDurationMs();
    if (editDurationMs <= 0) return;

    const editMs = this.state.currentTimeMs;
    const nextEditMs = editMs + deltaMs;

    if (nextEditMs >= editDurationMs) {
      this.cancelPlayFrame();
      this.updateState({ isPlaying: false, currentTimeMs: editDurationMs });
      return;
    }

    this.updateState({ currentTimeMs: nextEditMs });
    this.schedulePlayFrame();
  }

  private getOrderedClips(): ClippingStudioClip[] {
    const list = (this.state.clips ?? []).slice();
    list.sort((a, b) => a.order - b.order);
    return list;
  }

  /** Returns the clip that contains the given source time, or null if none. */
  private getClipContainingSourceTime(
    sourceMs: number,
  ): ClippingStudioClip | null {
    for (const c of this.getOrderedClips()) {
      if (sourceMs >= c.inMs && sourceMs <= c.outMs) return c;
    }
    return null;
  }

  /** Edit timeline position (ms) of the start of the clip with the given id. Used when seeking to clip start on click. */
  private getEditStartMsForClipId(clipId: string): number {
    let sum = 0;
    for (const c of this.getOrderedClips()) {
      if (c.id === clipId) return sum;
      sum += c.outMs - c.inMs;
    }
    return 0;
  }

  private sourceToEditTimeMsInternal(sourceMs: number): number {
    let sum = 0;
    for (const c of this.getOrderedClips()) {
      if (sourceMs >= c.inMs && sourceMs <= c.outMs)
        return sum + (sourceMs - c.inMs);
      sum += c.outMs - c.inMs;
    }
    return 0;
  }

  private editToSourceTimeMs(editMs: number): number {
    let sum = 0;
    for (const c of this.getOrderedClips()) {
      const clipLen = c.outMs - c.inMs;
      if (editMs <= sum + clipLen) return c.inMs + (editMs - sum);
      sum += clipLen;
    }
    const last = this.getOrderedClips()[this.getOrderedClips().length - 1];
    return last ? last.outMs : 0;
  }

  private cancelPlayFrame(): void {
    if (this.playRafId != null) {
      cancelAnimationFrame(this.playRafId);
      this.playRafId = null;
    }
  }

  seek(timeMs: number): void {
    const clamped = this.clampSeekToSegmentBounds(timeMs);
    if (DEBUG_SEEK) {
      csSeekLog("seek", {
        timeMsIn: timeMs,
        clamped,
        mode: this.state.mode,
        clipsLength: this.state.clips.length,
        prevCurrentTimeMs: this.state.currentTimeMs,
      });
    }
    if (this.state.isPlaying) {
      this.cancelPlayFrame();
      this.updateState({ isPlaying: false, currentTimeMs: clamped });
    } else {
      this.updateState({ currentTimeMs: clamped });
    }
  }

  /**
   * In EDIT mode with clips: timeMs is edit time; clamp to [0, getEditTimelineDurationMs()].
   * In SOURCE mode with zoomed segment bounds: clamp to [bounds.startMs, bounds.endMs].
   * Otherwise: clamp to [0, getTotalTimelineDurationMs()].
   */
  private clampSeekToSegmentBounds(timeMs: number): number {
    if (this.state.mode === "SOURCE" && this._zoomedSegmentBounds != null) {
      const { startMs, endMs } = this._zoomedSegmentBounds;
      return Math.max(startMs, Math.min(timeMs, endMs));
    }
    const durationMs = this.getTotalTimelineDurationMs();
    if (this.state.mode !== "EDIT" || this.state.clips.length === 0) {
      return Math.max(0, Math.min(timeMs, durationMs));
    }
    const editDurationMs = this.getEditTimelineDurationMs();
    if (editDurationMs <= 0) return Math.max(0, Math.min(timeMs, durationMs));
    const clamped = Math.max(0, Math.min(timeMs, editDurationMs));
    if (DEBUG_SEEK) {
      csSeekLog("clampSeekToSegmentBounds", {
        timeMsIn: timeMs,
        result: clamped,
        editDurationMs,
        mode: "EDIT",
      });
    }
    return clamped;
  }

  /**
   * Current time to show in the UI (timecode clock, timeline label).
   * In EDIT mode with clips: edit timeline position (state.currentTimeMs is already edit time).
   * Otherwise: source time.
   */
  getDisplayTimeMs(): number {
    if (!this.state) return 0;
    if (
      this.state.mode === "EDIT" &&
      this.state.clips &&
      this.state.clips.length > 0
    ) {
      return this.state.currentTimeMs;
    }
    return this.state.currentTimeMs;
  }

  goToIn(): void {
    const ordered = this.getOrderedClips();
    const target =
      this.state.mode === "EDIT" && ordered.length > 0
        ? 0
        : this.state.pendingInMs ?? 0;
    this.seek(target);
  }

  goToOut(): void {
    const durationMs = this.getTotalTimelineDurationMs();
    const ordered = this.getOrderedClips();
    const target =
      this.state.mode === "EDIT" && ordered.length > 0
        ? this.getEditTimelineDurationMs()
        : this.state.pendingOutMs ?? durationMs;
    this.seek(target);
  }

  frameDown(): void {
    if (!this.state.media) return;
    const frameMs = 1000 / this.state.media.fps;
    this.seek(this.state.currentTimeMs - frameMs);
  }

  frameUp(): void {
    if (!this.state.media) return;
    const frameMs = 1000 / this.state.media.fps;
    this.seek(this.state.currentTimeMs + frameMs);
  }

  /** Move playhead back by N minutes (m = minutes). */
  jumpBackMinutes(minutes: number): void {
    const ms = minutes * 60 * 1000;
    this.seek(Math.max(0, this.state.currentTimeMs - ms));
  }

  /** Seek to end of media (Live = end of timeline). */
  jumpToLive(): void {
    const durationMs = this.getTotalTimelineDurationMs();
    this.seek(durationMs);
  }

  markIn(): void {
    const nowMs = this.state.currentTimeMs;
    const durationMs = this.getTotalTimelineDurationMs();

    if (this.state.selectedClipId) {
      const clip = this.state.clips.find((c) => c.id === this.state.selectedClipId);
      if (clip) {
        const newInMs = Math.max(0, Math.min(nowMs, clip.outMs - 1));
        this.pushUndoSnapshot();
        this.updateState({
          clips: this.state.clips.map((c) =>
            c.id === clip.id ? { ...c, inMs: newInMs } : c,
          ),
        });
        this.persistProject();
        this.applyAiReframeForAffectedClips([
          { ...clip, inMs: newInMs },
        ]);
      }
      return;
    }
    if (this.state.selectedTextId) {
      const text = this.state.text.find((t) => t.id === this.state.selectedTextId);
      if (text) {
        const newInMs = Math.max(0, Math.min(nowMs, text.outMs - 1));
        this.updateText(this.state.selectedTextId, { inMs: newInMs });
      }
      return;
    }
    if (this.state.selectedGraphicId) {
      const graphic = this.state.graphics.find(
        (g) => g.id === this.state.selectedGraphicId,
      );
      if (graphic) {
        const newInMs = Math.max(0, Math.min(nowMs, graphic.outMs - 1));
        this.updateGraphic(this.state.selectedGraphicId, { inMs: newInMs });
      }
      return;
    }
    if (this.state.selectedMarkerId) {
      const marker = this.state.markers.find(
        (m) => m.id === this.state.selectedMarkerId,
      );
      if (marker && !marker.appendedSegmentId) {
        const newInMs = Math.max(0, Math.min(nowMs, durationMs));
        this.pushUndoSnapshot();
        this.updateState({
          markers: this.state.markers.map((m) =>
            m.id === marker.id ? { ...m, inMs: newInMs } : m,
          ),
        });
        this.persistProject();
      }
      if (marker) return;
    }

    const pendingOut = this.state.pendingOutMs;
    if (pendingOut != null && nowMs > pendingOut) {
      this.updateState({ pendingInMs: nowMs, pendingOutMs: null });
    } else {
      this.updateState({ pendingInMs: nowMs });
    }
  }

  markOut(): void {
    const nowMs = this.state.currentTimeMs;
    const durationMs = this.getTotalTimelineDurationMs();

    if (this.state.selectedClipId) {
      const clip = this.state.clips.find((c) => c.id === this.state.selectedClipId);
      if (clip) {
        const newOutMs = Math.max(clip.inMs + 1, Math.min(nowMs, durationMs));
        this.pushUndoSnapshot();
        this.updateState({
          clips: this.state.clips.map((c) =>
            c.id === clip.id ? { ...c, outMs: newOutMs } : c,
          ),
        });
        this.persistProject();
        this.applyAiReframeForAffectedClips([
          { ...clip, outMs: newOutMs },
        ]);
      }
      return;
    }
    if (this.state.selectedTextId) {
      const text = this.state.text.find((t) => t.id === this.state.selectedTextId);
      if (text) {
        const newOutMs = Math.max(text.inMs + 1, Math.min(nowMs, durationMs));
        this.updateText(this.state.selectedTextId, { outMs: newOutMs });
      }
      return;
    }
    if (this.state.selectedGraphicId) {
      const graphic = this.state.graphics.find(
        (g) => g.id === this.state.selectedGraphicId,
      );
      if (graphic) {
        const newOutMs = Math.max(graphic.inMs + 1, Math.min(nowMs, durationMs));
        this.updateGraphic(this.state.selectedGraphicId, { outMs: newOutMs });
      }
      return;
    }
    if (this.state.selectedMarkerId) {
      const marker = this.state.markers.find(
        (m) => m.id === this.state.selectedMarkerId,
      );
      if (marker && !marker.appendedSegmentId) {
        return;
      }
    }

    const pendingIn = this.state.pendingInMs;
    if (pendingIn != null && nowMs < pendingIn) {
      this.updateState({ pendingInMs: null, pendingOutMs: nowMs });
    } else {
      this.updateState({ pendingOutMs: nowMs });
    }
  }

  /**
   * Creates a new video segment from the current in/out points and adds it to the video track.
   * Persists the project. No-op if in or out is not set.
   * Returns the new clip id on success, null on failure.
   * @param customLabel Optional label for the segment (e.g. from Find Clip suggestion); defaults to "Segment N".
   * @param sourceRange Optional source-time range (inMs, outMs) for the new clip. When provided (e.g. from Find Clip in EDIT mode), used instead of pending In/Out.
   */
  addSegmentFromInOut(customLabel?: string, sourceRange?: { inMs: number; outMs: number }): string | null {
    const inMs = sourceRange?.inMs ?? this.state.pendingInMs;
    const outMs = sourceRange?.outMs ?? this.state.pendingOutMs;
    if (inMs == null || outMs == null) return null;
    this.pushUndoSnapshot();
    const durationMs = this.getTotalTimelineDurationMs();
    const startMs = Math.max(0, Math.min(inMs, outMs));
    const endMs = Math.min(durationMs, Math.max(inMs, outMs));
    if (startMs >= endMs) return null;

    const order = this.state.clips.length;
    const label =
      customLabel != null && String(customLabel).trim() !== ""
        ? String(customLabel).trim()
        : `Segment ${order + 1}`;
    const appendedWindow = this.resolveAppendedMamWindow(startMs, endMs);
    if (startMs < (this.state.media?.durationMs ?? 0) && endMs > (this.state.media?.durationMs ?? 0)) {
      return null;
    }
    if (startMs >= (this.state.media?.durationMs ?? 0) && appendedWindow == null) {
      return null;
    }

    const newClip: ClippingStudioClip = appendedWindow != null
      ? {
          id: `segment-${Date.now()}`,
          label,
          order,
          inMs: startMs,
          outMs: endMs,
          mamMediaId: appendedWindow.segment.mamMediaId,
          previewUrl: appendedWindow.segment.previewUrl,
          appendedSegmentId: appendedWindow.segment.id,
          ...this.mamAudioFieldsFromSelection(appendedWindow.segment),
        }
      : {
          id: `segment-${Date.now()}`,
          label,
          order,
          inMs: startMs,
          outMs: endMs,
        };
    this.updateState({
      clips: [...this.state.clips, newClip],
      pendingInMs: null,
      pendingOutMs: null,
    });
    this.persistProject();
    this.applyAiReframeForAffectedClips([newClip]);
    return newClip.id;
  }

  selectClip(clipId: string | null): void {
    if (clipId != null) {
      const clip = this.state.clips.find((c) => c.id === clipId);
      if (clip) {
        const seekMs =
          this.state.mode === "EDIT" && this.state.clips.length > 0
            ? this.getEditStartMsForClipId(clipId)
            : clip.inMs;
        this.seek(seekMs);
      }
    }
    this.updateState({ selectedClipId: clipId, selectedMarkerId: null });
  }

  selectMarker(markerId: string | null): void {
    if (markerId != null) {
      const marker = this.state.markers.find((m) => m.id === markerId);
      if (marker) {
        const seekMs =
          this.state.mode === "EDIT" && this.state.clips.length > 0
            ? this.sourceToEditTimeMsInternal(marker.inMs)
            : marker.inMs;
        this.seek(seekMs);
      }
    }
    this.updateState({ selectedMarkerId: markerId, selectedClipId: null });
  }

  /**
   * Creates a new marker at the current in point. Enabled when pendingInMs is set.
   * Default text "marker-N" where N is position in array (1-based). Persists the project.
   */
  addMarkerFromIn(): boolean {
    const inMs = this.state.pendingInMs;
    if (inMs == null) return false;
    this.pushUndoSnapshot();
    const durationMs = this.getTotalTimelineDurationMs();
    const clampedIn = Math.max(0, Math.min(inMs, durationMs));

    const n =
      this.state.markers.filter((m) => !m.appendedSegmentId).length + 1;
    const newMarker: ClippingStudioMarker = {
      id: `marker-${Date.now()}`,
      inMs: clampedIn,
      text: `marker-${n}`,
    };
    this.updateState({
      markers: [...this.state.markers, newMarker],
      pendingInMs: null,
      pendingOutMs: null,
    });
    this.persistProject();
    return true;
  }

  updateMarkerText(markerId: string, text: string): void {
    const marker = this.state.markers.find((m) => m.id === markerId);
    if (marker?.appendedSegmentId) return;
    this.pushUndoSnapshot();
    const markers = this.state.markers.map((m) =>
      m.id === markerId ? { ...m, text: text.trim() || m.text } : m,
    );
    this.updateState({ markers });
    this.persistProject();
  }

  /** Removes all video segments from the timeline and clears per-segment reframe data. */
  clearAllClips(): void {
    if (this.state.clips.length === 0) return;
    this.pushUndoSnapshot();
    const variants = { ...(this.state.destinationVariants ?? {}) };
    for (const destId of Object.keys(variants)) {
      variants[destId] = { ...variants[destId], segmentReframes: {} };
    }
    this.updateState({
      clips: [],
      destinationVariants: variants,
      selectedClipId: null,
      pendingInMs: null,
      pendingOutMs: null,
    });
    this.persistProject();
  }

  removeClip(clipId: string): void {
    this.pushUndoSnapshot();
    const clips = this.state.clips.filter((c) => c.id !== clipId);
    const variants = { ...(this.state.destinationVariants ?? {}) };
    for (const destId of Object.keys(variants)) {
      const seg = variants[destId].segmentReframes;
      if (seg && seg[clipId] !== undefined) {
        const next = { ...seg };
        delete next[clipId];
        variants[destId] = { ...variants[destId], segmentReframes: next };
      }
    }
    this.updateState({
      clips,
      destinationVariants: variants,
      selectedClipId:
        this.state.selectedClipId === clipId ? null : this.state.selectedClipId,
    });
    this.persistProject();
    if (
      this.state.mode === "EDIT" &&
      this.state.clips.length > 0 &&
      this.state.currentTimeMs > this.getEditTimelineDurationMs()
    ) {
      this.updateState({
        currentTimeMs: this.getEditTimelineDurationMs(),
      });
    }
  }

  removeMarker(markerId: string): void {
    this.pushUndoSnapshot();
    const markers = this.state.markers.filter((m) => m.id !== markerId);
    this.updateState({
      markers,
      selectedMarkerId:
        this.state.selectedMarkerId === markerId
          ? null
          : this.state.selectedMarkerId,
    });
    this.persistProject();
  }

  /**
   * Removes an appended MAM segment by its segment id. Finds the marker with appendedSegmentId and calls removeAppendedMamSegmentByMarkerId.
   */
  removeAppendedMamSegmentBySegmentId(segmentId: string): boolean {
    const marker = this.state.markers.find((m) => m.appendedSegmentId === segmentId);
    if (!marker) return false;
    return this.removeAppendedMamSegmentByMarkerId(marker.id);
  }

  /**
   * Removes an appended MAM segment by its marker (MAM source marker). Deletes the segment,
   * the marker, overlapping clips and other events; shifts remaining timecodes.
   */
  removeAppendedMamSegmentByMarkerId(markerId: string): boolean {
    const marker = this.state.markers.find((m) => m.id === markerId);
    if (!marker?.appendedSegmentId) return false;
    const appended = this.state.appendedMamSegments ?? [];
    const segment = appended.find((s) => s.id === marker.appendedSegmentId);
    if (!segment) return false;

    const mainDurationMs = this.state.media?.durationMs ?? 0;
    const segmentIndex = appended.findIndex((s) => s.id === segment.id);
    const segmentStartMs =
      mainDurationMs +
      appended
        .slice(0, segmentIndex)
        .reduce((sum, s) => sum + s.durationMs, 0);
    const segmentEndMs = segmentStartMs + segment.durationMs;
    const durationMs = segment.durationMs;

    const overlaps = (inMs: number, outMs: number): boolean =>
      !(outMs <= segmentStartMs || inMs >= segmentEndMs);

    this.pushUndoSnapshot();

    const clipsAfter = this.state.clips.filter(
      (c) => !overlaps(c.inMs, c.outMs),
    );
    const clipsShifted = clipsAfter.map((c) => {
      let inMs = c.inMs;
      let outMs = c.outMs;
      if (inMs > segmentEndMs) inMs -= durationMs;
      if (outMs > segmentEndMs) outMs -= durationMs;
      return { ...c, inMs, outMs };
    });
    const clipsFinal = clipsShifted.filter((c) => c.inMs < c.outMs);

    const textAfter = this.state.text.filter(
      (t) => !overlaps(t.inMs, t.outMs),
    );
    const textShifted = textAfter
      .map((t) => {
        let inMs = t.inMs;
        let outMs = t.outMs;
        if (inMs > segmentEndMs) inMs -= durationMs;
        if (outMs > segmentEndMs) outMs -= durationMs;
        return { ...t, inMs, outMs };
      })
      .filter((t) => t.inMs < t.outMs);

    const graphicsAfter = this.state.graphics.filter(
      (g) => !overlaps(g.inMs, g.outMs),
    );
    const graphicsShifted = graphicsAfter
      .map((g) => {
        let inMs = g.inMs;
        let outMs = g.outMs;
        if (inMs > segmentEndMs) inMs -= durationMs;
        if (outMs > segmentEndMs) outMs -= durationMs;
        return { ...g, inMs, outMs };
      })
      .filter((g) => g.inMs < g.outMs);

    const prevEdits = this.state.subtitleEdits ?? {};
    const nextSubtitleEdits: Record<string, ClippingStudioSubtitleEvent[]> = {};
    for (const [lang, cues] of Object.entries(prevEdits)) {
      const shifted = this.shiftSubtitleCuesAfterRemovedWindow(
        cues,
        segmentStartMs,
        segmentEndMs,
        durationMs,
      );
      if (shifted.length > 0) {
        nextSubtitleEdits[lang] = shifted;
      }
    }
    const originals = this.state.subtitleOriginalsByLanguage ?? {};
    const tracksByLanguage = this.buildSubtitleTracksByLanguage(
      originals,
      nextSubtitleEdits,
    );
    const activeLang = normalizeSubtitleLanguageKey(
      this.state.activeSubtitleLanguage,
    );

    const markersWithout = this.state.markers.filter((m) => m.id !== markerId);
    const markersShifted = markersWithout.map((m) => ({
      ...m,
      inMs: m.inMs >= segmentEndMs ? m.inMs - durationMs : m.inMs,
    }));

    const appendedAfter = appended.filter((s) => s.id !== segment.id);

    let currentTimeMs = this.state.currentTimeMs;
    if (currentTimeMs >= segmentEndMs) {
      currentTimeMs -= durationMs;
    } else if (currentTimeMs > segmentStartMs) {
      currentTimeMs = segmentStartMs;
    }

    const removedClipIds = new Set(
      this.state.clips
        .filter((c) => overlaps(c.inMs, c.outMs))
        .map((c) => c.id),
    );
    const selectedClipId =
      this.state.selectedClipId && removedClipIds.has(this.state.selectedClipId)
        ? null
        : this.state.selectedClipId;

    this.updateState({
      clips: clipsFinal,
      markers: markersShifted,
      text: textShifted,
      graphics: graphicsShifted,
      subtitleEdits: nextSubtitleEdits,
      subtitleTracksByLanguage: tracksByLanguage,
      subtitles: activeLang
        ? [...(tracksByLanguage[activeLang] ?? [])]
        : [],
      appendedMamSegments: appendedAfter,
      currentTimeMs,
      selectedMarkerId:
        this.state.selectedMarkerId === markerId
          ? null
          : this.state.selectedMarkerId,
      selectedClipId,
    });
    this.persistProject();
    return true;
  }

  /** Reorder clips (EDIT mode). newOrderedIds must list all clip ids in desired order. */
  reorderClips(newOrderedIds: string[]): void {
    this.pushUndoSnapshot();
    const byId = new Map(this.state.clips.map((c) => [c.id, c]));
    const reordered: ClippingStudioClip[] = newOrderedIds
      .map((id, index) => {
        const clip = byId.get(id);
        if (!clip) return null;
        return { ...clip, order: index };
      })
      .filter((c): c is ClippingStudioClip => c != null);
    if (reordered.length !== this.state.clips.length) return;
    this.updateState({ clips: reordered });
    this.persistProject();
  }

  formatTime(timeMs: number): string {
    const totalSeconds = Math.floor(timeMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const ms = Math.floor(timeMs % 1000);
    const pad = (n: number, len = 2) => n.toString().padStart(len, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}.${pad(ms, 3)}`;
  }

  clear(): void {
    this.stop();
    this._undoStack = [];
    this._redoStack = [];
    this.stateSubject.next(createInitialClippingStudioState());
  }

  // --- Transcript & subtitles ---

  async loadTranscriptTracks(mediaId: number): Promise<void> {
    this.updateState({
      loadingTranscriptTracks: true,
      transcriptLoadError: null,
    });
    try {
      const tracks = await this.transcriptRepository.getTranscriptTracks(mediaId);
      const source =
        tracks.find((t) => t.isOriginal)?.language ?? tracks[0]?.language ?? "";
      const active = source || (tracks[0]?.language ?? "");
      const updates: Partial<ClippingStudioState> = {
        transcriptTracks: tracks,
        sourceAudioLanguage: source,
        activeTranscriptLanguage: active,
        loadingTranscriptTracks: false,
        transcriptLoadError: null,
      };
      this.updateState(updates);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load transcript tracks.";
      this.updateState({
        loadingTranscriptTracks: false,
        transcriptLoadError: message,
        transcriptTracks: [],
        sourceAudioLanguage: "",
        activeTranscriptLanguage: "",
      });
    }
  }

  setActiveTranscriptLanguage(lang: string): void {
    this.updateState({ activeTranscriptLanguage: lang });
  }

  async regenerateTranscript(): Promise<void> {
    const mediaId = this.state.media?.id;
    const lang = this.state.sourceAudioLanguage;
    if (mediaId == null || !lang) return;
    this.updateState({ isRegeneratingTranscript: true });
    await this.transcriptRepository.regenerateTranscript(mediaId, lang);
    await new Promise((resolve) =>
      setTimeout(resolve, (Math.floor(Math.random() * 4) + 2) * 1000),
    );
    this.updateState({ isRegeneratingTranscript: false });
  }

  async updateTranscriptBlockText(
    blockId: number,
    text: string,
  ): Promise<void> {
    const mediaId = this.state.media?.id;
    const lang = this.state.activeTranscriptLanguage;
    if (mediaId == null || !lang) return;
    await this.transcriptRepository.updateTranscriptBlock(
      mediaId,
      lang,
      blockId,
      text,
    );
    const tracks = this.state.transcriptTracks.map((t) =>
      t.language === lang
        ? {
            ...t,
            blocks: t.blocks.map((b) =>
              b.id === blockId ? { ...b, text } : b,
            ),
          }
        : t,
    );
    this.updateState({ transcriptTracks: tracks });
  }

  async generateSubtitlesFromTranscript(): Promise<void> {
    const mediaId = this.state.media?.id;
    const lang = this.resolveActiveSubtitleEditLanguage(
      this.state.activeTranscriptLanguage,
    );
    if (mediaId == null || !lang) return;
    const subs =
      await this.transcriptRepository.generateSubtitlesFromTranscript(
        mediaId,
        lang,
      );
    if (!subs.length) return;
    this.pushUndoSnapshot();
    const edits = { ...(this.state.subtitleEdits ?? {}) };
    const withLang = subs.map((s) => ({
      ...s,
      language: lang,
      fromMainSource: false,
    }));
    edits[lang] = [...(edits[lang] ?? []), ...withLang];
    this.commitSubtitleEdits(edits, { activeSubtitleLanguage: lang });
    this.persistProject();
  }

  async importSubtitles(content: string, format: "srt" | "vtt"): Promise<void> {
    const subs = await this.transcriptRepository.importSubtitles(
      content,
      format,
    );
    if (!subs.length) return;
    this.pushUndoSnapshot();
    const edits = { ...(this.state.subtitleEdits ?? {}) };
    const trackKey = allocateImportedSubtitleTrackKey(format, [
      ...Object.keys(edits),
      ...Object.keys(this.state.subtitleTracksByLanguage ?? {}),
      ...Object.keys(this.state.subtitleOriginalsByLanguage ?? {}),
    ]);
    edits[trackKey] = subs.map((s) => ({
      ...s,
      language: trackKey,
      fromMainSource: false,
    }));
    this.commitSubtitleEdits(edits, { activeSubtitleLanguage: trackKey });
    this.persistProject();
  }

  /** Remove an imported track (`srt-N` / `vtt-N`) by deleting its subtitleEdits key. */
  removeImportedSubtitleTrack(trackKey: string): void {
    const key = normalizeSubtitleLanguageKey(trackKey);
    if (!isImportedSubtitleTrackKey(key)) return;
    const edits = { ...(this.state.subtitleEdits ?? {}) };
    if (!(key in edits)) return;

    this.pushUndoSnapshot();
    delete edits[key];

    const wasActive =
      normalizeSubtitleLanguageKey(this.state.activeSubtitleLanguage) === key;
    const extras: Partial<ClippingStudioState> = {};
    if (wasActive) {
      const remainingKeys = Object.keys(
        this.buildSubtitleTracksByLanguage(
          this.state.subtitleOriginalsByLanguage ?? {},
          edits,
        ),
      );
      extras.activeSubtitleLanguage = remainingKeys[0];
      extras.selectedSubtitleId = null;
    }

    this.commitSubtitleEdits(edits, extras);
    this.persistProject();
  }

  async exportSubtitles(format: "srt" | "vtt"): Promise<string> {
    return this.transcriptRepository.exportSubtitles(
      this.state.subtitles,
      format,
    );
  }

  addSubtitle(sub: ClippingStudioSubtitleEvent): void {
    const lang = this.resolveActiveSubtitleEditLanguage(sub.language);
    if (!lang) return;
    this.pushUndoSnapshot();
    const subWithFlag = {
      ...sub,
      language: lang,
      fromMainSource: false,
    };
    const edits = { ...(this.state.subtitleEdits ?? {}) };
    edits[lang] = [...(edits[lang] ?? []), subWithFlag];
    this.commitSubtitleEdits(edits, { activeSubtitleLanguage: lang });
    this.persistProject();
  }

  updateSubtitleText(id: string, text: string): void {
    const lang = this.resolveActiveSubtitleEditLanguage();
    if (!lang) return;
    this.pushUndoSnapshot();
    const segment = this.state.subtitles.find((s) => s.id === id);
    const edits = { ...(this.state.subtitleEdits ?? {}) };
    const langEdits = [...(edits[lang] ?? []).filter((e) => e.id !== id)];
    langEdits.push({
      id,
      StartTimeMS: segment?.StartTimeMS ?? 0,
      EndTimeMS: segment?.EndTimeMS ?? 0,
      Text: text,
      language: lang,
    });
    edits[lang] = langEdits;
    this.commitSubtitleEdits(edits);
    this.persistProject();
  }

  splitSubtitle(id: string): void {
    const sub = this.state.subtitles.find((s) => s.id === id);
    if (!sub) return;
    const lang = this.resolveActiveSubtitleEditLanguage(sub.language);
    if (!lang) return;
    const mid = Math.floor((sub.StartTimeMS + sub.EndTimeMS) / 2);
    const newId = `sub-${Date.now()}`;
    const newSub: ClippingStudioSubtitleEvent = {
      id: newId,
      StartTimeMS: mid,
      EndTimeMS: sub.EndTimeMS,
      Text: sub.Text,
      language: lang,
      fromMainSource: false,
    };
    this.pushUndoSnapshot();
    const edits = { ...(this.state.subtitleEdits ?? {}) };
    const langEdits = (edits[lang] ?? []).filter((e) => e.id !== id);
    langEdits.push({ ...sub, EndTimeMS: mid, language: lang }, newSub);
    edits[lang] = langEdits;
    this.commitSubtitleEdits(edits, { activeSubtitleLanguage: lang });
    this.persistProject();
  }

  removeSubtitle(id: string): void {
    const lang = this.resolveActiveSubtitleEditLanguage();
    if (!lang) return;
    const sub = this.state.subtitles.find((s) => s.id === id);
    if (!sub) return;
    this.pushUndoSnapshot();
    const clearSelection =
      this.state.selectedSubtitleId === id ? null : this.state.selectedSubtitleId;
    const edits = { ...(this.state.subtitleEdits ?? {}) };
    const langEdits = (edits[lang] ?? []).filter((e) => e.id !== id);
    langEdits.push({
      ...sub,
      Text: "",
      EndTimeMS: sub.StartTimeMS,
      language: lang,
    });
    edits[lang] = langEdits;
    this.commitSubtitleEdits(edits, { selectedSubtitleId: clearSelection });
    this.persistProject();
  }

  setActiveSubtitleLanguage(lang: string): void {
    const tracks = this.state.subtitleTracksByLanguage;
    if (!tracks?.[lang]) return;
    this.updateState({
      activeSubtitleLanguage: lang,
      subtitles: [...tracks[lang]],
    });
  }

  /** Apply subtitle style from the style panel (Apply to all clips). */
  applySubtitleStyle(style: ClippingStudioSubtitleStyle): void {
    this.pushUndoSnapshot();
    this.updateState({ subtitleStyle: style });
    this.persistProject();
  }

  // --- Graphics ---

  addGraphic(item: {
    id: string;
    type: Exclude<GraphicType, "gfx">;
    mainText: string;
    subText: string;
    color: string;
    durationLabel: string;
    logoKey?: string;
  }): void {
    this.pushUndoSnapshot();
    const durationSec = this.parseDurationLabel(item.durationLabel);
    const inMs = this.state.currentTimeMs;
    const outMs = inMs + durationSec * 1000;
    const isLogo = item.type === "logo" && item.logoKey;
    const isFruitIcon = item.type === "fruit-icon";
    const isEmoji = item.type === "emoji";
    const graphic: ClippingStudioGraphicEvent = {
      id: `graphic-${Date.now()}`,
      inMs,
      outMs,
      type: item.type,
      mainText: item.mainText,
      subText: item.subText,
      color: item.color,
      ...(isLogo && {
        logoKey: item.logoKey as import("app/js/src/domain/clipping-studio/clipping-studio-project").LogoKey,
        scale: 1,
        rotationDeg: 0,
      }),
      ...(isFruitIcon && {
        iconKey: "Icon 1",
        scale: 1,
        rotationDeg: 0,
      }),
      ...(isEmoji && {
        emojiChar: "😀",
        scale: 1,
        rotationDeg: 0,
      }),
    };
    const destId = this.currentPreviewDestinationId;
    if (destId == null) {
      this.updateState({
        graphics: [...this.state.graphics, graphic],
        selectedGraphicId: graphic.id,
      });
    } else {
      const variants = { ...(this.state.destinationVariants ?? {}) };
      const existing = variants[destId] ?? {};
      const graphicOnly = [...(existing.graphicOnly ?? []), graphic];
      variants[destId] = { ...existing, graphicOnly };
      this.updateState({
        destinationVariants: variants,
        selectedGraphicId: graphic.id,
      });
    }
    this.persistProject();
  }

  addGfxGraphic(item: {
    id?: string;
    gfxInstanceId: number;
    gfxParams?: Record<string, string>;
    durationMs?: number;
    isSqz?: boolean;
  }): ClippingStudioGraphicEvent {
    this.pushUndoSnapshot();
    const inMs = this.state.currentTimeMs;
    const outMs = inMs + (item.durationMs ?? 10_000);
    const graphic: ClippingStudioGraphicEvent = {
      id: item.id ?? `gfx-${Date.now()}-${item.gfxInstanceId}`,
      inMs,
      outMs,
      type: "gfx",
      mainText: "",
      subText: "",
      color: "",
      gfxInstanceId: item.gfxInstanceId,
      gfxParams: item.gfxParams ?? {},
      ...(item.isSqz === true ? { isSqz: true } : {}),
      rotationDeg: 0,
    };
    const destId = this.currentPreviewDestinationId;
    if (destId == null) {
      this.updateState({
        graphics: [...this.state.graphics, graphic],
        selectedGraphicId: null,
      });
    } else {
      const variants = { ...(this.state.destinationVariants ?? {}) };
      const existing = variants[destId] ?? {};
      const graphicOnly = [...(existing.graphicOnly ?? []), graphic];
      variants[destId] = { ...existing, graphicOnly };
      this.updateState({
        destinationVariants: variants,
        selectedGraphicId: null,
      });
    }
    this.persistProject();
    return graphic;
  }

  selectGraphic(id: string | null): void {
    this.updateState({ selectedGraphicId: id });
  }

  updateGraphic(
    id: string,
    changes: Partial<ClippingStudioGraphicEvent>,
    options?: { skipUndo?: boolean; skipPersist?: boolean },
  ): void {
    if (!options?.skipUndo) {
      this.pushUndoSnapshot();
    }
    const destId = this.currentPreviewDestinationId;
    if (destId == null) {
      const graphics = this.state.graphics.map((g) =>
        g.id === id ? { ...g, ...changes } : g,
      );
      this.updateState({ graphics });
    } else {
      const variants = { ...(this.state.destinationVariants ?? {}) };
      const existing = variants[destId] ?? {};
      const baseIds = new Set(this.state.graphics.map((g) => g.id));
      if (baseIds.has(id)) {
        const graphicOverrides = { ...(existing.graphicOverrides ?? {}) };
        graphicOverrides[id] = { ...(graphicOverrides[id] ?? {}), ...changes };
        variants[destId] = { ...existing, graphicOverrides };
      } else {
        const graphicOnly = (existing.graphicOnly ?? []).map((g) =>
          g.id === id ? { ...g, ...changes } : g,
        );
        variants[destId] = { ...existing, graphicOnly };
      }
      this.updateState({ destinationVariants: variants });
    }
    if (!options?.skipPersist) {
      this.persistProject();
    }
  }

  /** Call once when a graphic drag ends so the project is saved after the last position update. */
  endGraphicDrag(): void {
    this.persistProject();
  }

  /** Call once when a graphic drag starts so a single undo step captures the pre-drag state. */
  beginGraphicMove(): void {
    this.pushUndoSnapshot();
  }

  removeGraphic(id: string): void {
    this.pushUndoSnapshot();
    const destId = this.currentPreviewDestinationId;
    if (destId == null) {
      this.updateState({
        graphics: this.state.graphics.filter((g) => g.id !== id),
        selectedGraphicId:
          this.state.selectedGraphicId === id
            ? null
            : this.state.selectedGraphicId,
      });
    } else {
      const variants = { ...(this.state.destinationVariants ?? {}) };
      const existing = variants[destId] ?? {};
      const baseIds = new Set(this.state.graphics.map((g) => g.id));
      if (baseIds.has(id)) {
        const graphicOverrides = { ...(existing.graphicOverrides ?? {}) };
        delete graphicOverrides[id];
        const graphicExcludedIds = Array.from(
          new Set([...(existing.graphicExcludedIds ?? []), id]),
        );
        variants[destId] = {
          ...existing,
          graphicOverrides: Object.keys(graphicOverrides).length > 0 ? graphicOverrides : undefined,
          graphicExcludedIds,
        };
      } else {
        const graphicOnly = (existing.graphicOnly ?? []).filter((g) => g.id !== id);
        variants[destId] = { ...existing, graphicOnly: graphicOnly.length > 0 ? graphicOnly : undefined };
      }
      this.updateState({
        destinationVariants: variants,
        selectedGraphicId:
          this.state.selectedGraphicId === id ? null : this.state.selectedGraphicId,
      });
    }
    this.persistProject();
  }

  private parseDurationLabel(label: string): number {
    const parts = label.split(":");
    if (parts.length === 2)
      return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    return 5;
  }

  // --- Text ---

  addText(preset: {
    text: string;
    fontSize?: number;
    fontWeight?: string;
    fontColor?: string;
    fontStyle?: string;
    fontFamily?: string;
    alignment?: ClippingStudioTextEvent["alignment"];
    bold?: boolean;
    italic?: boolean;
    backgroundColor?: string;
    backgroundPadding?: number;
    backgroundBorderRadius?: number;
    dropShadow?: boolean;
    shadowColor?: string;
    shadowBlur?: number;
    shadowOffsetX?: number;
    shadowOffsetY?: number;
  }): void {
    const currentState = this.stateSubject.value;
    const nowMs = currentState.currentTimeMs ?? 0;

    let outMs: number;
    if (currentState.mode === "EDIT" && currentState.clips.length > 0) {
      const sourceMs = this.getCurrentSourceTimeMs();
      const clip = this.getClipContainingSourceTime(sourceMs);
      if (!clip) return; // playhead not inside any segment; skip adding
      outMs = this.sourceToEditTimeMs(clip.outMs);
    } else {
      outMs = nowMs + 5000;
    }

    const normX = 0.5;
    const normY = 0.5;
    const { fontWeight, bold } = normalizeTextWeight(preset);
    const fontStyle = preset.fontStyle ?? "normal";
    const italic = preset.italic ?? fontStyle === "italic";

    this.pushUndoSnapshot();
    const newEvent: ClippingStudioTextEvent = {
      id: "text-" + Date.now(),
      inMs: nowMs,
      outMs,
      text: preset.text,
      fontSize: preset.fontSize ?? 48,
      fontWeight,
      fontColor: preset.fontColor ?? "#ffffff",
      fontStyle,
      fontFamily: preset.fontFamily ?? "Inter",
      alignment: preset.alignment ?? "center",
      bold,
      italic,
      backgroundColor: preset.backgroundColor,
      backgroundPadding: preset.backgroundPadding,
      backgroundBorderRadius: preset.backgroundBorderRadius,
      dropShadow: preset.dropShadow,
      shadowColor: preset.shadowColor,
      shadowBlur: preset.shadowBlur,
      shadowOffsetX: preset.shadowOffsetX,
      shadowOffsetY: preset.shadowOffsetY,
      x: normX,
      y: normY,
    };
    const destId = this.currentPreviewDestinationId;
    if (destId == null) {
      this.updateState({
        text: [...currentState.text, newEvent],
        selectedTextId: newEvent.id,
      });
    } else {
      const variants = { ...(currentState.destinationVariants ?? {}) };
      const existing = variants[destId] ?? {};
      const textOnly = [...(existing.textOnly ?? []), newEvent];
      variants[destId] = { ...existing, textOnly };
      this.updateState({
        destinationVariants: variants,
        selectedTextId: newEvent.id,
      });
    }
    this.persistProject();
  }

  selectText(id: string | null): void {
    this.updateState({ selectedTextId: id });
  }

  setSelectedSubtitleId(id: string | null): void {
    this.updateState({ selectedSubtitleId: id });
  }

  updateText(
    id: string,
    incomingChanges: Partial<ClippingStudioTextEvent>,
    options?: { skipUndo?: boolean; skipPersist?: boolean },
  ): void {
    if (!options?.skipUndo) {
      this.pushUndoSnapshot();
    }
    const changes = this.withNormalizedTextWeight(incomingChanges);
    const destId = this.currentPreviewDestinationId;
    if (destId == null) {
      const text = this.state.text.map((t) =>
        t.id === id ? { ...t, ...changes } : t,
      );
      this.updateState({ text });
    } else {
      const variants = { ...(this.state.destinationVariants ?? {}) };
      const existing = variants[destId] ?? {};
      const baseIds = new Set(this.state.text.map((t) => t.id));
      if (baseIds.has(id)) {
        const textOverrides = { ...(existing.textOverrides ?? {}) };
        textOverrides[id] = { ...(textOverrides[id] ?? {}), ...changes };
        variants[destId] = { ...existing, textOverrides };
      } else {
        const textOnly = (existing.textOnly ?? []).map((t) =>
          t.id === id ? { ...t, ...changes } : t,
        );
        variants[destId] = { ...existing, textOnly };
      }
      this.updateState({ destinationVariants: variants });
    }
    if (!options?.skipPersist) {
      this.persistProject();
    }
  }

  /**
   * Keeps `fontWeight` and `bold` consistent and limited to the weights the render can draw,
   * so a style edit never persists a weight that only the preview would honor.
   */
  private withNormalizedTextWeight(
    changes: Partial<ClippingStudioTextEvent>,
  ): Partial<ClippingStudioTextEvent> {
    if (changes.fontWeight === undefined && changes.bold === undefined) {
      return changes;
    }
    return { ...changes, ...normalizeTextWeight(changes) };
  }

  /** Call once when a text drag starts so a single undo step captures the pre-drag state. */
  beginTextMove(): void {
    this.pushUndoSnapshot();
  }

  /** Call once when a text drag ends so the project is saved after the last position update. */
  endTextDrag(): void {
    this.persistProject();
  }

  removeText(id: string): void {
    this.pushUndoSnapshot();
    const destId = this.currentPreviewDestinationId;
    if (destId == null) {
      this.updateState({
        text: this.state.text.filter((t) => t.id !== id),
        selectedTextId:
          this.state.selectedTextId === id ? null : this.state.selectedTextId,
      });
    } else {
      const variants = { ...(this.state.destinationVariants ?? {}) };
      const existing = variants[destId] ?? {};
      const baseIds = new Set(this.state.text.map((t) => t.id));
      if (baseIds.has(id)) {
        const textOverrides = { ...(existing.textOverrides ?? {}) };
        delete textOverrides[id];
        const textExcludedIds = Array.from(
          new Set([...(existing.textExcludedIds ?? []), id]),
        );
        variants[destId] = {
          ...existing,
          textOverrides: Object.keys(textOverrides).length > 0 ? textOverrides : undefined,
          textExcludedIds,
        };
      } else {
        const textOnly = (existing.textOnly ?? []).filter((t) => t.id !== id);
        variants[destId] = { ...existing, textOnly: textOnly.length > 0 ? textOnly : undefined };
      }
      this.updateState({
        destinationVariants: variants,
        selectedTextId:
          this.state.selectedTextId === id ? null : this.state.selectedTextId,
      });
    }
    this.persistProject();
  }

  setProjectTitle(title: string): void {
    this.pushUndoSnapshot();
    this.updateState({ projectTitle: title });
  }

  /**
   * Appends a MAM video segment to the source timeline (SOURCE mode only).
   * Uses getMediaPreviewFileUrl (preview, not transcoded). Segment is added at the end.
   * Creates a MAM source marker at the segment start; label is shown on the marker.
   */
  async addAppendedMamSegment(
    mamMediaId: number,
    durationMs: number,
    label?: string,
  ): Promise<AppendedMamSegment | void> {
    if (this.state.mode !== "SOURCE" || !this.state.media) return;
    if (durationMs <= 0) return;
    const [previewUrl, defaultAudio] = await Promise.all([
      this.mediaRepository.getMediaPreviewFileUrl(mamMediaId),
      resolveDefaultAudioForMedia(
        mamMediaId,
        this.mediaRepository,
        this.audioRepository,
      ),
    ]);
    this.pushUndoSnapshot();
    const appended = this.state.appendedMamSegments ?? [];
    const mainDurationMs = this.state.media.durationMs ?? 0;
    const segmentStartMs =
      mainDurationMs +
      appended.reduce((sum, seg) => sum + seg.durationMs, 0);
    const newSegment: AppendedMamSegment = {
      id: `mam-seg-${Date.now()}`,
      mamMediaId,
      durationMs,
      previewUrl,
      label: label ?? "MAM source",
      ...(defaultAudio != null && {
        audioMediaId: defaultAudio.defaultAudioMediaId,
        defaultAudioMediaId: defaultAudio.defaultAudioMediaId,
        defaultAudioPreviewUrl: defaultAudio.defaultAudioPreviewUrl,
      }),
    };
    const newMarker: ClippingStudioMarker = {
      id: `marker-mam-${newSegment.id}`,
      inMs: segmentStartMs,
      text: newSegment.label,
      appendedSegmentId: newSegment.id,
    };
    this.updateState({
      appendedMamSegments: [...appended, newSegment],
      markers: [...this.state.markers, newMarker],
    });
    this.persistProject();
    this.seek(segmentStartMs);
    return newSegment;
  }

  /**
   * Adds a MAM video as a new clip at the end of the edit sequence (EDIT mode only).
   * Uses getMediaPreviewFileUrl (preview, not transcoded). Clip is movable, deletable, and only visible in Edit mode.
   */
  async addMamClipInEditMode(
    mamMediaId: number,
    durationMs: number,
    label?: string,
  ): Promise<ClippingStudioClip | void> {
    if (this.state.mode !== "EDIT" || !this.state.media) return;
    if (durationMs <= 0) return;
    let previewUrl: string;
    let defaultAudio: Awaited<ReturnType<typeof resolveDefaultAudioForMedia>>;
    try {
      [previewUrl, defaultAudio] = await Promise.all([
        this.mediaRepository.getMediaPreviewFileUrl(mamMediaId),
        resolveDefaultAudioForMedia(
          mamMediaId,
          this.mediaRepository,
          this.audioRepository,
        ),
      ]);
    } catch {
      return;
    }
    if (!previewUrl?.trim()) return;
    this.pushUndoSnapshot();
    const ordered = this.getOrderedClips();
    const endSourceMs =
      ordered.length > 0
        ? Math.max(...ordered.map((c) => c.outMs))
        : 0;
    const maxOrder =
      ordered.length > 0 ? Math.max(...ordered.map((c) => c.order)) : -1;
    const newClip: ClippingStudioClip = {
      id: `mam-clip-${Date.now()}`,
      label: label ?? "MAM source",
      order: maxOrder + 1,
      inMs: endSourceMs,
      outMs: endSourceMs + durationMs,
      mamMediaId,
      previewUrl,
      ...(defaultAudio != null && {
        audioMediaId: defaultAudio.defaultAudioMediaId,
        defaultAudioMediaId: defaultAudio.defaultAudioMediaId,
        defaultAudioPreviewUrl: defaultAudio.defaultAudioPreviewUrl,
      }),
    };
    const editStartNewClip = this.getEditTimelineDurationMs();
    this.updateState({
      clips: [...this.state.clips, newClip],
    });
    this.persistProject();
    this.seek(editStartNewClip);
    this.notifySegmentAddedFromPanel(newClip.id);
    return newClip;
  }

  /** Persists current state to the repository and returns the promise. */
  saveProject(): Promise<void> {
    const prepared = this.prepareProjectSave();
    if (!prepared) {
      return Promise.resolve();
    }
    return this.executeTrackedProjectSave(prepared);
  }

  /** Submits the current project for publication (Publish and Exit/Continue). */
  submitProjectForPublication(cloneProject: boolean): Promise<void> {
    const projectId = this.state.projectId;
    if (projectId == null || projectId <= 0) {
      return Promise.reject(new Error("Project not loaded."));
    }
    return this.repository.submitProjectForPublication(projectId, cloneProject);
  }

  private updateState(partial: Partial<ClippingStudioState>): void {
    this.stateSubject.next({ ...this.state, ...partial });
    this.updateZoomedSegmentBoundsFromState();
  }

  private buildProjectFromState(): ClippingStudioProject | null {
    const mediaId = this.state.media?.id;
    if (mediaId == null) return null;
    const selectedUids = new Set(this.getSelectedDestinationUidsForSave());
    const variants = this.state.destinationVariants;
    const variantEntries =
      variants != null
        ? Object.entries(variants).filter(([uid]) => selectedUids.has(uid))
        : [];
    const destinationVariants =
      variantEntries.length > 0
        ? Object.fromEntries(
            variantEntries.map(([k, v]) => [
              k,
              {
                textOverrides: v.textOverrides ? { ...v.textOverrides } : undefined,
                graphicOverrides: v.graphicOverrides ? { ...v.graphicOverrides } : undefined,
                textOnly: v.textOnly?.map((t) => ({ ...t })) ?? undefined,
                graphicOnly: v.graphicOnly?.map((g) => ({ ...g })) ?? undefined,
                textExcludedIds: v.textExcludedIds ? [...v.textExcludedIds] : undefined,
                graphicExcludedIds: v.graphicExcludedIds ? [...v.graphicExcludedIds] : undefined,
                segmentReframes: v.segmentReframes
                  ? Object.fromEntries(
                      Object.entries(v.segmentReframes).map(([segId, keyframes]) => [
                        segId,
                        keyframes.map((kf: ReframeKeyframe) => ({
                          timeMs: kf.timeMs,
                          config: {
                            ...kf.config,
                            crop: kf.config.crop ? { ...kf.config.crop } : undefined,
                            topCrop: kf.config.topCrop ? { ...kf.config.topCrop } : undefined,
                            bottomCrop: kf.config.bottomCrop ? { ...kf.config.bottomCrop } : undefined,
                          },
                        })),
                      ]),
                    )
                  : undefined,
              },
            ]),
          )
        : undefined;
    const fps = this.state.fps ?? this.state.media?.fps ?? 30;
    return {
      mediaId,
      title: this.state.projectTitle,
      clipPreview: this.state.clipPreview ?? null,
      mainSource: this.state.mainSource ?? undefined,
      findClipRecentPrompts: this.state.findClipRecentPrompts ?? undefined,
      fps,
      video: this.state.clips.map((c) => {
        const audioMediaId = readPersistedMamAudioMediaId(c);
        return {
          id: c.id,
          label: c.label,
          order: c.order,
          inMs: c.inMs,
          outMs: c.outMs,
          thumbnailFirstFrameUrl: c.thumbnailFirstFrameUrl ?? null,
          thumbnailLastFrameUrl: c.thumbnailLastFrameUrl ?? null,
          ...(c.mamMediaId != null && { mamMediaId: c.mamMediaId }),
          ...(c.previewUrl != null && c.previewUrl !== "" && { previewUrl: c.previewUrl }),
          ...(c.appendedSegmentId != null && { appendedSegmentId: c.appendedSegmentId }),
          ...(audioMediaId != null && { audioMediaId }),
        };
      }),
      markers: this.state.markers.map((m) => ({
        id: m.id,
        inMs: m.inMs,
        text: m.text,
        ...(m.appendedSegmentId != null && { appendedSegmentId: m.appendedSegmentId }),
      })),
      subtitles: [],
      subtitleEdits: this.state.subtitleEdits
        ? { ...this.state.subtitleEdits }
        : undefined,
      subtitleStyle: this.state.subtitleStyle ?? undefined,
      text: [...this.state.text],
      graphics: [...this.state.graphics],
      audio: [...(this.state.audio ?? [])],
      destinationVariants,
      appendedMamSegments: (this.state.appendedMamSegments ?? []).map((s) => {
        const audioMediaId = readPersistedMamAudioMediaId(s);
        return {
          id: s.id,
          mamMediaId: s.mamMediaId,
          durationMs: s.durationMs,
          ...(s.previewUrl != null && s.previewUrl !== "" && { previewUrl: s.previewUrl }),
          ...(s.label != null && { label: s.label }),
          ...(audioMediaId != null && { audioMediaId }),
        };
      }),
    };
  }

  private persistProject(): void {
    const prepared = this.prepareProjectSave();
    if (!prepared) {
      return;
    }
    void this.executeTrackedProjectSave(prepared);
  }

  private prepareProjectSave(): {
    projectId: number;
    project: ClippingStudioProject;
    publishPayload: PublishMetadataSavePayload | undefined;
  } | null {
    const projectId = this.state.projectId;
    const project = this.buildProjectFromState();
    if (projectId == null || projectId <= 0 || !project) {
      return null;
    }
    return {
      projectId,
      project,
      publishPayload: this.getPublishMetadataForSave(),
    };
  }

  /**
   * Runs the same repository save as before; only observes the promise for UI status.
   * Returns the original repository promise unchanged (resolve/reject behavior preserved).
   */
  private executeTrackedProjectSave(prepared: {
    projectId: number;
    project: ClippingStudioProject;
    publishPayload: PublishMetadataSavePayload | undefined;
  }): Promise<void> {
    const generation = ++this.saveGeneration;
    this.saveStatusSubject.next("saving");
    const promise = this.repository.saveProject(
      prepared.projectId,
      prepared.project,
      prepared.publishPayload,
    );
    void promise.then(
      () => {
        if (generation === this.saveGeneration) {
          this.saveStatusSubject.next("saved");
        }
      },
      () => {
        if (generation === this.saveGeneration) {
          this.saveStatusSubject.next("error");
        }
      },
    );
    return promise;
  }

  private getSelectedDestinationUidsForSave(): string[] {
    try {
      return this.injector.get(PublishStateService).selectedDestinationIds;
    } catch {
      return [];
    }
  }

  private getPublishMetadataForSave(): PublishMetadataSavePayload | undefined {
    try {
      return this.injector.get(PublishStateService).getPublishMetadataForProjectSave();
    } catch {
      return undefined;
    }
  }

  private clipPreviewSnapshotRequestSubject = new Subject<{
    aspectRatio?: ClipPreviewAspectRatio;
    destinationUid?: string | null;
  }>();
  /** Emits when something (e.g. thumbnail tab) requests a clip preview snapshot. Root component subscribes and runs same logic as Snapshot button. */
  readonly clipPreviewSnapshotRequest$ = this.clipPreviewSnapshotRequestSubject.asObservable();

  private initialClipPreviewSnapshotRequestSubject = new Subject<void>();
  /** Emits when media has just been loaded; root should take an initial snapshot after a short delay so the preview is filled before the user opens the metadata tab. */
  readonly initialClipPreviewSnapshotRequest$ =
    this.initialClipPreviewSnapshotRequestSubject.asObservable();

  /** Request capture of clip preview snapshot (same as clicking Snapshot button). Root component must subscribe and save a clip-preview model. */
  requestClipPreviewSnapshot(
    aspectRatio?: ClipPreviewAspectRatio,
    destinationUid?: string | null,
  ): void {
    this.clipPreviewSnapshotRequestSubject.next({ aspectRatio, destinationUid });
  }

  async captureClipPreviewFrameAt(
    timeMs: number,
    options?: {
      overlayTimeMs?: number | null;
      destinationUid?: string | null;
      aspectRatio?: ClipPreviewAspectRatio;
    },
  ): Promise<string | null> {
    return (await this.clipPreviewFrameProvider?.(timeMs, options)) ?? null;
  }

  getClipPreviewDisplayUrl(): string | null {
    return getClipPreviewDisplayUrl(this.state.clipPreview);
  }

  getClipPreviewModel() {
    return parseClipPreviewModel(this.state.clipPreview);
  }

  async getClipPreviewEditorSourceImage(): Promise<string | null> {
    const model = parseClipPreviewModel(this.state.clipPreview);
    if (!model) return null;
    if (model.sourceType === "uploaded-image") return model.uploadedImage;
    return resolveVideoSnapshotEditorSource({
      captureFrame: () =>
        this.clipPreviewFrameProvider?.(model.source.timeMs, {
          overlayTimeMs: model.source.fullEditMs ?? null,
        }) ?? Promise.resolve(null),
      fallbackPreviewBase64: model.previewBase64 ?? null,
    });
  }

  setClipPreviewSnapshot(args: {
    previewBase64: string;
    aspectRatio: ClipPreviewAspectRatio;
    timeMs?: number;
    fullEditMs?: number | null;
  }): void {
    const model = createVideoSnapshotClipPreviewModel({
      previewBase64: args.previewBase64,
      mediaId: this.state.media?.id ?? null,
      timeMs: args.timeMs ?? this.getCurrentSourceTimeMs(),
      fullEditMs: args.fullEditMs ?? null,
      aspectRatio: args.aspectRatio,
    });
    this.updateState({ clipPreview: serializeClipPreviewModel(model) });
    this.persistProject();
  }

  setClipPreviewFromEditorResult(args: {
    previewBase64: string;
    aspectRatio: ClipPreviewAspectRatio;
    editor: ClipPreviewEditorState;
    uploadedImage?: string | null;
  }): void {
    const model = updateClipPreviewAfterEditorApply({
      current: this.state.clipPreview,
      previewBase64: args.previewBase64,
      aspectRatio: args.aspectRatio,
      editor: args.editor,
      uploadedImage: args.uploadedImage,
    });
    this.updateState({ clipPreview: serializeClipPreviewModel(model) });
    this.persistProject();
  }

  /** Sets the raw persisted clip-preview model payload and persists the project. */
  setRawClipPreviewModel(model: string | null): void {
    this.updateState({ clipPreview: model });
    this.persistProject();
  }

  /** Sets per-segment first/last frame thumbnails and persists the project. */
  setClipThumbnails(
    clipId: string,
    thumbnailFirstFrameUrl: string | null,
    thumbnailLastFrameUrl: string | null,
  ): void {
    const clip = this.state.clips.find((c) => c.id === clipId);
    if (!clip) return;
    const clips = this.state.clips.map((c) =>
      c.id === clipId
        ? {
            ...c,
            thumbnailFirstFrameUrl: thumbnailFirstFrameUrl ?? undefined,
            thumbnailLastFrameUrl: thumbnailLastFrameUrl ?? undefined,
          }
        : c,
    );
    this.updateState({ clips });
    this.persistProject();
  }

  /** Sets per-segment reframe keyframes for a destination and persists the project. */
  setSegmentReframesForDestination(
    destinationId: string,
    segmentReframeKeyframes: Record<string, ReframeKeyframe[]>,
  ): void {
    const variants = { ...(this.state.destinationVariants ?? {}) };
    const existing = variants[destinationId] ?? {};
    variants[destinationId] = {
      ...existing,
      segmentReframes: { ...segmentReframeKeyframes },
    };
    this.updateState({ destinationVariants: variants });
    this.persistProject();
  }

  /**
   * Runs AI reframe for the given clips (fire-and-forget). Merges computed keyframes into existing segmentReframes per destination.
   * MAM clips (mamMediaId set) are excluded – they are not analyzed by AI.
   */
  private applyAiReframeForAffectedClips(affectedClips: ClippingStudioClip[]): void {
    const sourceClips = affectedClips.filter((c) => c.mamMediaId == null);
    const focusUrl = this.state.media?.focusUrl?.trim();
    const destMap = this._destinationAspectRatios;
    if (!focusUrl || sourceClips.length === 0 || Object.keys(destMap).length === 0) {
      return;
    }
    this.aiReframeService
      .applyForClips(sourceClips, focusUrl, destMap)
      .then((perDest) => {
        const state = this.stateSubject.value;
        const variants = { ...(state.destinationVariants ?? {}) };
        for (const [destId, clipKeyframes] of Object.entries(perDest)) {
          const existing = variants[destId]?.segmentReframes ?? {};
          const merged = { ...existing };
          for (const [clipId, keyframes] of Object.entries(clipKeyframes)) {
            merged[clipId] = keyframes;
          }
          variants[destId] = {
            ...(variants[destId] ?? {}),
            segmentReframes: merged,
          };
        }
        this.updateState({ destinationVariants: variants });
        this.persistProject();
      })
      .catch(() => {});
  }

  /**
   * Fills missing AI reframe keyframes for clips that have none (e.g. after load). Fire-and-forget.
   * Only sets keyframes for (dest, clip) pairs that currently have no keyframes.
   * MAM clips (mamMediaId set) are excluded – they are not analyzed by AI.
   */
  private applyAiReframeFillMissing(): void {
    const focusUrl = this.state.media?.focusUrl?.trim();
    const destMap = this._destinationAspectRatios;
    if (!focusUrl || Object.keys(destMap).length === 0) return;
    const clips = this.state.clips;
    if (clips.length === 0) return;

    const clipsNeedingReframe: ClippingStudioClip[] = [];
    for (const destId of Object.keys(destMap)) {
      const seg = this.state.destinationVariants?.[destId]?.segmentReframes;
      for (const clip of clips) {
        if (clip.mamMediaId != null) continue;
        const kfs = seg?.[clip.id];
        if (!kfs || kfs.length === 0) {
          if (!clipsNeedingReframe.find((c) => c.id === clip.id)) {
            clipsNeedingReframe.push(clip);
          }
        }
      }
    }
    if (clipsNeedingReframe.length === 0) return;

    this.aiReframeService
      .applyForClips(clipsNeedingReframe, focusUrl, destMap)
      .then((perDest) => {
        const state = this.stateSubject.value;
        const variants = { ...(state.destinationVariants ?? {}) };
        for (const [destId, clipKeyframes] of Object.entries(perDest)) {
          const existing = variants[destId]?.segmentReframes ?? {};
          const merged = { ...existing };
          for (const [clipId, keyframes] of Object.entries(clipKeyframes)) {
            const had = existing[clipId];
            if (!had || had.length === 0) {
              merged[clipId] = keyframes;
            }
          }
          variants[destId] = {
            ...(variants[destId] ?? {}),
            segmentReframes: merged,
          };
        }
        this.updateState({ destinationVariants: variants });
        this.persistProject();
      })
      .catch(() => {});
  }
}


